import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import MainContext from "../../Context/MainContext/MainContext";
import MUIPersonAddIcon from "@mui/icons-material/PersonAdd";
import MUIAddIcon from "@mui/icons-material/Add";
import MUICloseIcon from "@mui/icons-material/Close";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUIDescriptionIcon from "@mui/icons-material/Description";
import MUIFaxIcon from "@mui/icons-material/Fax";
import MUIForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import MUIHourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MUIInfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MUILinkIcon from "@mui/icons-material/Link";
import MUIOpenInFullIcon from "@mui/icons-material/OpenInFull";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import MUIRefreshIcon from "@mui/icons-material/Refresh";
import MUIRemoveIcon from "@mui/icons-material/Remove";
import MUIRestoreIcon from "@mui/icons-material/Restore";
import MUIRotateRightIcon from "@mui/icons-material/RotateRight";
import MUISearchIcon from "@mui/icons-material/Search";
import MUIWarningAmberIcon from "@mui/icons-material/WarningAmber";
import MUICircularProgress from "@mui/material/CircularProgress";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIAutoComplete from "@mui/material/Autocomplete";
import MUIAvatar from "@mui/material/Avatar";
import MUIBox from "@mui/material/Box";
import MUIChip from "@mui/material/Chip";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIDivider from "@mui/material/Divider";
import MUIGrid from "@mui/material/Grid";
import MUIIconButton from "@mui/material/IconButton";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIList from "@mui/material/List";
import MUIListItemAvatar from "@mui/material/ListItemAvatar";
import MUIListItemButton from "@mui/material/ListItemButton";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIListItemText from "@mui/material/ListItemText";
import MUILink from "@mui/material/Link";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIPaper from "@mui/material/Paper";
import MUISkeleton from "@mui/material/Skeleton";
import MUIStack from "@mui/material/Stack";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import LayerFunctional from "../../LayerFunctional";
import Menu from "../../Component/Menu";
import WidgetDashboardRow from "../../Component/WidgetDashboardRow";
import api from "../../Seating/Security/api";
import DialogConfirm from "../../Component/Dialog/DialogConfirm";
import DialogInboundFileTransfer from "../../Component/Dialog/DialogInboundFileTransfer";
import EmptyState from "../../Component/EmptyState";
import Progress from "../../Component/Progress";
import Dashboard from "../../Utility/Crud/Dashboard";
import ActivityFileType from "../../Utility/Crud/ActivityFileType";
import Setting from "../../Utility/Setting";
import User from "../../Utility/Crud/User";
import Format from "../../Utility/Format";
import InboundFile from "../../Utility/Crud/InboundFile";
import OrderStatus from "../../Utility/Crud/OrderStatus";
import OrderStatusReason from "../../Utility/Crud/OrderStatusReason";
import Patient from "../../Utility/Crud/Patient";

/**
 * Layer-specific configuration and option builders for the inbound-files workspace.
 */
class InboundFilesConfiguration {
  static sectionIds = {
    inbox: "inbox",
    deleted: "deleted",
    linked: "linked",
  };
  static sections = [
    { id: InboundFilesConfiguration.sectionIds.inbox, label: "Inbox" },
    { id: InboundFilesConfiguration.sectionIds.linked, label: "Linked" },
    { id: InboundFilesConfiguration.sectionIds.deleted, label: "Deleted" },
  ];
  static autoRefreshIntervalMilliseconds = 5 * 60 * 1000;
  static openOrderPollIntervalMilliseconds = 2.5 * 1000;
  static createNewPatientOptionValue = "create-new-patient";
  static createNewPatientOption = {
    inputLabel: "Create New Patient",
    label: "Create New Patient",
    searchText: "",
    title: "Create New Patient",
    value: InboundFilesConfiguration.createNewPatientOptionValue,
  };
  static desktopWorkspaceHeight = "calc(100vh - 82px)";
  static dashboardLayoutHeight = "calc(100vh - 82px)";
  static pageWidths = {
    default: 800,
    enlarged: 1200,
  };
  static keyboardShortcuts = [
    { description: "Show previous/next page", keys: "← / → / ↑ / ↓" },
    { description: "Rotate clockwise", keys: "R" },
    { description: "Rotate counter-clockwise", keys: "⇧ + R" },
    { description: "Open in full screen", keys: "F" },
    { description: "Zoom full-screen document", keys: "Ctrl + wheel / + / -" },
  ];

  /**
   * Gets an autocomplete option from an open-order record.
   * @param {object|number|string} openOrder The open-order record.
   * @param {object} orderStatusesIndexed Order statuses indexed by identifier.
   * @param {object} orderStatusReasonsIndexed Order status reasons indexed by identifier.
   * @returns {object|null} The autocomplete option.
   */
  static getOpenOrderOption(
    openOrder,
    orderStatusesIndexed,
    orderStatusReasonsIndexed,
  ) {
    /**
     * Converts a supported value to trimmed text.
     * @param {*} value The value to convert.
     * @returns {string} The converted text.
     */
    const getText = (value) => {
      return typeof value === "number" || typeof value === "string"
        ? String(value).trim()
        : "";
    };
    const identifier =
      typeof openOrder === "number" || typeof openOrder === "string"
        ? getText(openOrder)
        : getText(
            openOrder?.id ??
              openOrder?.orderActivityId ??
              openOrder?.orderId ??
              openOrder?.uploadToOrderActivityId,
          );

    if (!identifier) {
      return null;
    }

    const patient = openOrder?.patient || {};
    const patientName =
      getText(openOrder?.patientName) ||
      getText(Patient.getDisplayName(patient));
    const formattedDateOfBirth = Format.localDate(
      getText(openOrder?.dateOfBirth ?? patient?.dateOfBirth),
    );
    const orderStatusIdentifier = getText(
      openOrder?.orderStatus ?? openOrder?.orderStatusId ?? openOrder?.status,
    );
    const statusLabel = getText(
      openOrder?.statusName ||
        OrderStatus.getDisplayName(orderStatusesIndexed[orderStatusIdentifier]),
    );
    const orderStatusReasonIdentifier = getText(
      openOrder?.orderStatusReasonId ??
        openOrder?.reasonId ??
        openOrder?.orderStatusReason?.id ??
        openOrder?.orderStatusReason,
    );
    const statusReasonLabel = getText(
      openOrder?.reason ||
        openOrder?.reasonName ||
        openOrder?.statusReasonName ||
        OrderStatusReason.getDisplayName(
          orderStatusReasonsIndexed[orderStatusReasonIdentifier],
        ) ||
        OrderStatusReason.getDisplayName(openOrder?.orderStatusReason),
    );
    const title = [patientName || "Unknown Patient", formattedDateOfBirth]
      .filter(Boolean)
      .join(" • ");
    const metadata = [`Order #${identifier}`, statusLabel, statusReasonLabel]
      .filter(Boolean)
      .join(" • ");
    const searchText = [
      identifier,
      patientName,
      formattedDateOfBirth,
      formattedDateOfBirth ? formattedDateOfBirth.replace(/\D/g, "") : "",
      getText(openOrder?.orderType),
      statusLabel,
      statusReasonLabel,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return {
      inputLabel: patientName
        ? `${patientName} • #${identifier}`
        : `#${identifier}`,
      label: `${title} • ${metadata}`,
      metaLine: metadata,
      record: openOrder,
      searchText: searchText,
      title: title,
      value: identifier,
    };
  }

  /**
   * Gets open-order records from an API response.
   * @param {object|array} openOrdersResponse The open-orders response.
   * @returns {array} The open-order records.
   */
  static getOpenOrderRecordsFromResponse(openOrdersResponse) {
    if (Array.isArray(openOrdersResponse)) {
      return openOrdersResponse;
    }

    const openOrdersData = openOrdersResponse?.data || openOrdersResponse || {};
    const orderActivityRecords = openOrdersData.orderActivity || {};
    const patientRecords = openOrdersData.patient || {};

    return Object.keys(orderActivityRecords).map((orderActivityIdentifier) => {
      const orderActivity = orderActivityRecords[orderActivityIdentifier];
      const patientIdentifier = String(
        orderActivity?.patientId ??
          orderActivity?.patientsId ??
          orderActivity?.patient_id ??
          "",
      );

      return {
        ...orderActivity,
        id: orderActivity?.id ?? orderActivityIdentifier,
        patient:
          patientRecords[patientIdentifier] || orderActivity?.patient || {},
      };
    });
  }
}

/**
 * Gets the workspace folder for an inbound-file record.
 * @param {object} inboundFile The inbound-file record.
 * @returns {string} The folder identifier.
 */
function getInboundFileFolder(inboundFile) {
  if (inboundFile.uploadedFileId !== null) {
    return InboundFilesConfiguration.sectionIds.linked;
  }

  if (inboundFile.deleted) {
    return InboundFilesConfiguration.sectionIds.deleted;
  }

  return InboundFilesConfiguration.sectionIds.inbox;
}

/**
 * Determines whether an inbox file is waiting to be processed.
 * @param {object} inboundFile The inbound-file record.
 * @returns {boolean} Whether the inbox file is waiting for processing.
 */
function isInboundFileWaitingForProcessing(inboundFile) {
  return (
    getInboundFileFolder(inboundFile) ===
      InboundFilesConfiguration.sectionIds.inbox &&
    inboundFile.processedAt === null
  );
}

/**
 * Gets the display detail text for an inbound-file record.
 * @param {object} inboundFile The inbound-file record.
 * @returns {string} The detail text.
 */
function getInboundFileDetailText(inboundFile) {
  const pageCount = Number(inboundFile.pageCount);

  return [
    Format.dateTime(inboundFile.createdAt),
    pageCount ? `${pageCount} ${pageCount === 1 ? "page" : "pages"}` : "",
    Format.fileSize(inboundFile.filesizeBytes),
  ]
    .filter(Boolean)
    .join(" • ");
}

/**
 * Gets the cache key for an inbound-file thumbnail.
 * @param {string} inboundFileIdentifier The inbound-file identifier.
 * @param {number} pageNumber The page number.
 * @param {number} width The requested thumbnail width.
 * @returns {string} The thumbnail cache key.
 */
function getInboundFileThumbnailCacheKey(
  inboundFileIdentifier,
  pageNumber,
  width,
) {
  return `${inboundFileIdentifier}:${pageNumber}:width-${width}`;
}

/**
 * Caches inbound-file thumbnail requests and browser object URLs for the life
 * of the current application session.
 */
class InboundFileThumbnailCache {
  static #sourceByCacheKey = new Map();
  static #requestByCacheKey = new Map();

  /**
   * Gets a cached thumbnail source.
   * @param {string} inboundFileIdentifier The inbound file identifier.
   * @param {number} pageNumber The page number.
   * @param {number} width The requested thumbnail width.
   * @returns {string|undefined} The cached thumbnail source.
   */
  static getSource(inboundFileIdentifier, pageNumber, width) {
    return this.#sourceByCacheKey.get(
      getInboundFileThumbnailCacheKey(inboundFileIdentifier, pageNumber, width),
    );
  }

  /**
   * Loads and caches a thumbnail source.
   * @param {string} inboundFileIdentifier The inbound file identifier.
   * @param {number} pageNumber The page number.
   * @param {number} width The requested thumbnail width.
   * @returns {Promise<string>} The thumbnail source.
   */
  static async loadSource(inboundFileIdentifier, pageNumber, width) {
    const cacheKey = getInboundFileThumbnailCacheKey(
      inboundFileIdentifier,
      pageNumber,
      width,
    );
    const cachedThumbnailSource = this.#sourceByCacheKey.get(cacheKey);

    if (cachedThumbnailSource) {
      return cachedThumbnailSource;
    }

    const activeThumbnailRequest = this.#requestByCacheKey.get(cacheKey);
    if (activeThumbnailRequest) {
      return activeThumbnailRequest;
    }

    const thumbnailRequest = api
      .getFile("/inboundFile/getThumbnail", {
        inboundFileId: inboundFileIdentifier,
        pageNumber: pageNumber,
        width: width,
      })
      .then((thumbnailBlob) => {
        const thumbnailSource = window.URL.createObjectURL(thumbnailBlob);
        this.#sourceByCacheKey.set(cacheKey, thumbnailSource);
        this.#requestByCacheKey.delete(cacheKey);
        return thumbnailSource;
      })
      .catch((error) => {
        this.#requestByCacheKey.delete(cacheKey);
        throw error;
      });

    this.#requestByCacheKey.set(cacheKey, thumbnailRequest);
    return thumbnailRequest;
  }
}

/**
 * Gets list-item properties without the React key used by MUI Autocomplete.
 * @param {object} optionProperties The MUI option properties.
 * @returns {object} The list-item properties.
 */
function getAutocompleteListItemProperties(optionProperties) {
  const listItemProperties = Object.assign({}, optionProperties);
  delete listItemProperties.key;
  return listItemProperties;
}
/**
 * Renders a selectable inbound-file list item.
 * @param {object} props Component properties.
 * @param {object} props.inboundFile The inbound file record.
 * @param {string} props.inboundFileTypeName The inbound file type name.
 * @param {boolean} props.isSelected Whether this item is selected.
 * @param {boolean} props.isWaitingForProcessing Whether processing is incomplete.
 * @param {function} props.onSelect Selects this inbound file.
 * @param {string} props.recipientText The formatted recipient text.
 * @returns {React.ReactElement} The inbound-file list item.
 */
function InboundFileListItem(props) {
  const InboundFileTypeIcon = props.isWaitingForProcessing
    ? MUIHourglassEmptyIcon
    : props.inboundFileTypeName.toLowerCase().includes("fax")
      ? MUIFaxIcon
      : MUIDescriptionIcon;

  /**
   * Handles selection of this inbound file.
   * @returns {void}
   */
  const handleSelect = () => {
    props.onSelect(props.inboundFile.id);
  };

  return (
    <MUIListItemButton
      selected={props.isSelected}
      onClick={handleSelect}
      sx={{
        alignItems: "center",
        borderRadius: 1,
        opacity: props.isWaitingForProcessing ? 0.7 : 1,
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        pl: 1.25,
        pr: 3.5,
      }}
    >
      <MUIListItemAvatar sx={{ minWidth: 55 }}>
        <MUIAvatar
          sx={(theme) => ({
            backgroundColor: props.isSelected
              ? theme.palette.primary.main
              : theme.palette.action.selected,
            color: props.isSelected
              ? theme.palette.primary.contrastText
              : theme.palette.text.secondary,
            height: 38,
            width: 38,
            opacity: props.isWaitingForProcessing ? 0.6 : 1,
          })}
        >
          <InboundFileTypeIcon />
        </MUIAvatar>
      </MUIListItemAvatar>
      <MUIListItemText
        primary={InboundFile.getDisplayName(props.inboundFile)}
        secondary={
          <>
            {props.recipientText ? (
              <MUITypography
                component="span"
                display="block"
                variant="caption"
                color="primary.main"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.35 }}
              >
                To: {props.recipientText}
              </MUITypography>
            ) : null}
            <MUITypography
              component="span"
              display="block"
              variant="caption"
              color="text.secondary"
              sx={{ marginBottom: 0, paddingBottom: 0, lineHeight: 1.2 }}
            >
              {getInboundFileDetailText(props.inboundFile)}
            </MUITypography>
          </>
        }
        sx={{ minWidth: 0 }}
        primaryTypographyProps={{
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.2,
          sx: {
            overflowWrap: "anywhere",
            whiteSpace: "normal",
            wordBreak: "break-word",
            mb: 0.65,
          },
        }}
        secondaryTypographyProps={{
          component: "div",
        }}
      />
    </MUIListItemButton>
  );
}

/**
 * Renders and controls the preview for a selected inbound file.
 * @param {object} props Component properties.
 * @param {object|null} props.inboundFile The selected inbound file.
 * @param {boolean} props.isPreviewUnavailable Whether preview images are unavailable.
 * @param {boolean} props.isWaitingForProcessing Whether file processing is incomplete.
 * @param {string} props.linkedOrderIdentifier The linked order identifier.
 * @param {function} props.onAttachmentPagesChange Reports page rotation metadata for linking.
 * @param {boolean} props.shouldShowUnavailableForMissingThumbnail Whether a missing thumbnail makes the preview unavailable.
 * @returns {React.ReactElement} The inbound-file preview.
 */
function InboundFilePreview(props) {
  const thumbnailStripReference = useRef(null);
  const fullViewThumbnailSidebarReference = useRef(null);
  const fullViewScrollReference = useRef(null);
  const fullViewInitialPageNumberReference = useRef(1);
  const fullViewInitialScrollAnimationFrameReference = useRef(null);
  const fullViewNavigationPageNumberReference = useRef(null);
  const fullViewNavigationScrollEndTimeoutReference = useRef(null);
  const [isFullViewOpen, setIsFullViewOpen] = useState(false);
  const [fullViewZoomPercentage, setFullViewZoomPercentage] = useState(100);
  const [selectedPageNumber, setSelectedPageNumber] = useState(1);
  const [rotationDegreesByPageNumber, setRotationDegreesByPageNumber] =
    useState({});
  const [thumbnailSourceByCacheKey, setThumbnailSourceByCacheKey] = useState(
    {},
  );
  const [isThumbnailPreviewUnavailable, setIsThumbnailPreviewUnavailable] =
    useState(false);
  const inboundFileIdentifier = props.inboundFile ? props.inboundFile.id : null;
  const isPreviewUnavailable =
    props.isPreviewUnavailable || isThumbnailPreviewUnavailable;
  const fullViewPageWidth =
    isFullViewOpen && fullViewZoomPercentage > 130
      ? InboundFilesConfiguration.pageWidths.enlarged
      : InboundFilesConfiguration.pageWidths.default;

  /**
   * Gets the page numbers for the selected inbound file.
   * @returns {array} The page numbers.
   */
  const pageNumbers = useMemo(() => {
    if (!props.inboundFile) {
      return [];
    }

    if (Array.isArray(props.inboundFile.pages)) {
      return props.inboundFile.pages.map((page) => {
        return Number(page.pageNumber || page);
      });
    }

    const pageCount = Number(
      props.inboundFile.pageCount ||
        props.inboundFile.numberOfPages ||
        props.inboundFile.totalPages,
    );

    const inboundFilePageNumbers = [];
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      inboundFilePageNumbers.push(pageNumber);
    }

    return inboundFilePageNumbers;
  }, [props.inboundFile]);

  // Reports the current upload metadata whenever page rotations change.
  useEffect(() => {
    props.onAttachmentPagesChange(
      pageNumbers.map((pageNumber) => {
        const rotationDegrees = rotationDegreesByPageNumber[pageNumber] || 0;

        return {
          pageNumber: pageNumber,
          rotate: ((rotationDegrees % 360) + 360) % 360,
        };
      }),
    );
  }, [pageNumbers, props.onAttachmentPagesChange, rotationDegreesByPageNumber]);

  // Keeps the selected thumbnail visible while navigating pages in either viewer.
  useEffect(() => {
    [thumbnailStripReference, fullViewThumbnailSidebarReference].forEach(
      (thumbnailContainerReference) => {
        const selectedThumbnail = thumbnailContainerReference.current
          ? thumbnailContainerReference.current.querySelector(
              `[data-preview-page-number="${selectedPageNumber}"]`,
            )
          : null;

        if (selectedThumbnail) {
          selectedThumbnail.scrollIntoView({
            block: "nearest",
            inline: "nearest",
          });
        }
      },
    );
  }, [pageNumbers, selectedPageNumber]);

  // Loads and caches the default-width version of every page for the selected file.
  useEffect(() => {
    if (
      !inboundFileIdentifier ||
      pageNumbers.length === 0 ||
      isPreviewUnavailable
    ) {
      setThumbnailSourceByCacheKey({});
      return;
    }

    let isCancelled = false;
    const cachedSourceByCacheKey = {};

    pageNumbers.forEach((pageNumber) => {
      const cacheKey = getInboundFileThumbnailCacheKey(
        inboundFileIdentifier,
        pageNumber,
        InboundFilesConfiguration.pageWidths.default,
      );
      const cachedSource = InboundFileThumbnailCache.getSource(
        inboundFileIdentifier,
        pageNumber,
        InboundFilesConfiguration.pageWidths.default,
      );

      if (cachedSource) {
        cachedSourceByCacheKey[cacheKey] = cachedSource;
      }
    });

    setThumbnailSourceByCacheKey(cachedSourceByCacheKey);

    pageNumbers.forEach((pageNumber) => {
      const cacheKey = getInboundFileThumbnailCacheKey(
        inboundFileIdentifier,
        pageNumber,
        InboundFilesConfiguration.pageWidths.default,
      );

      InboundFileThumbnailCache.loadSource(
        inboundFileIdentifier,
        pageNumber,
        InboundFilesConfiguration.pageWidths.default,
      )
        .then((thumbnailSource) => {
          if (isCancelled) {
            return;
          }

          setThumbnailSourceByCacheKey((currentSources) => {
            return {
              ...currentSources,
              [cacheKey]: thumbnailSource,
            };
          });
        })
        .catch((error) => {
          if (
            !isCancelled &&
            props.shouldShowUnavailableForMissingThumbnail &&
            error &&
            typeof error === "object" &&
            error.status === 404
          ) {
            setIsThumbnailPreviewUnavailable(true);
          }
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [
    inboundFileIdentifier,
    isPreviewUnavailable,
    pageNumbers,
    props.shouldShowUnavailableForMissingThumbnail,
  ]);

  // Loads the enlarged version of each page when scrolling makes it current.
  useEffect(() => {
    if (
      fullViewPageWidth !== InboundFilesConfiguration.pageWidths.enlarged ||
      !inboundFileIdentifier ||
      pageNumbers.length === 0 ||
      isPreviewUnavailable
    ) {
      return;
    }

    let isCancelled = false;
    const cacheKey = getInboundFileThumbnailCacheKey(
      inboundFileIdentifier,
      selectedPageNumber,
      InboundFilesConfiguration.pageWidths.enlarged,
    );

    InboundFileThumbnailCache.loadSource(
      inboundFileIdentifier,
      selectedPageNumber,
      InboundFilesConfiguration.pageWidths.enlarged,
    )
      .then((thumbnailSource) => {
        if (isCancelled) {
          return;
        }

        setThumbnailSourceByCacheKey((currentSources) => {
          return {
            ...currentSources,
            [cacheKey]: thumbnailSource,
          };
        });
      })
      .catch((error) => {
        if (
          !isCancelled &&
          props.shouldShowUnavailableForMissingThumbnail &&
          error &&
          typeof error === "object" &&
          error.status === 404
        ) {
          setIsThumbnailPreviewUnavailable(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    fullViewPageWidth,
    inboundFileIdentifier,
    isPreviewUnavailable,
    pageNumbers,
    props.shouldShowUnavailableForMissingThumbnail,
    selectedPageNumber,
  ]);

  /**
   * Gets the cached source for a page thumbnail.
   * @param {number} pageNumber The page number.
   * @param {number} width The preferred thumbnail width.
   * @returns {string} The thumbnail source.
   */
  const getThumbnailSource = (
    pageNumber,
    width = InboundFilesConfiguration.pageWidths.default,
  ) => {
    const cacheKey = getInboundFileThumbnailCacheKey(
      inboundFileIdentifier,
      pageNumber,
      width,
    );
    const defaultCacheKey = getInboundFileThumbnailCacheKey(
      inboundFileIdentifier,
      pageNumber,
      InboundFilesConfiguration.pageWidths.default,
    );

    return (
      thumbnailSourceByCacheKey[cacheKey] ||
      thumbnailSourceByCacheKey[defaultCacheKey] ||
      ""
    );
  };

  /**
   * Rotates the selected page clockwise.
   * @returns {void}
   */
  const handleRotateClockwise = () => {
    setRotationDegreesByPageNumber((currentRotations) => {
      return {
        ...currentRotations,
        [selectedPageNumber]: (currentRotations[selectedPageNumber] || 0) + 90,
      };
    });
  };

  /**
   * Rotates the selected page counter-clockwise.
   * @returns {void}
   */
  const handleRotateCounterClockwise = () => {
    setRotationDegreesByPageNumber((currentRotations) => {
      return {
        ...currentRotations,
        [selectedPageNumber]: (currentRotations[selectedPageNumber] || 0) - 90,
      };
    });
  };

  /**
   * Moves the selected page by an offset.
   * @param {number} pageOffset The page-index offset.
   * @returns {void}
   */
  const moveSelectedPage = (pageOffset) => {
    if (pageNumbers.length === 0) {
      return;
    }

    setSelectedPageNumber((currentPageNumber) => {
      const currentPageIndex = pageNumbers.indexOf(currentPageNumber);
      const safePageIndex = currentPageIndex === -1 ? 0 : currentPageIndex;
      const nextPageIndex = Math.min(
        Math.max(safePageIndex + pageOffset, 0),
        pageNumbers.length - 1,
      );

      return pageNumbers[nextPageIndex];
    });
  };

  /**
   * Opens the continuous full-screen viewer at the selected preview page.
   * @returns {void}
   */
  const handleOpenFullView = () => {
    fullViewInitialPageNumberReference.current = selectedPageNumber;
    setIsFullViewOpen(true);
  };

  /**
   * Closes the full-screen viewer while retaining its current page selection.
   * @returns {void}
   */
  const handleCloseFullView = () => {
    setIsFullViewOpen(false);
  };

  /**
   * Changes the full-screen document zoom by a percentage offset.
   * @param {number} zoomOffset The percentage-point offset.
   * @param {number|null} anchorClientX The horizontal viewport anchor, or null for the viewport center.
   * @param {number|null} anchorClientY The vertical viewport anchor, or null for the viewport center.
   * @returns {void}
   */
  const changeFullViewZoom = useCallback(
    (zoomOffset, anchorClientX = null, anchorClientY = null) => {
      const scrollContainer = fullViewScrollReference.current;
      const scrollContainerBounds = scrollContainer.getBoundingClientRect();
      const zoomAnchorClientX =
        anchorClientX === null
          ? scrollContainerBounds.left + scrollContainerBounds.width / 2
          : anchorClientX;
      const zoomAnchorClientY =
        anchorClientY === null
          ? scrollContainerBounds.top + scrollContainerBounds.height / 2
          : anchorClientY;
      let anchorPageElement = scrollContainer.querySelector(
        "[data-full-view-page-content]",
      );
      let anchorPageDistance = Number.POSITIVE_INFINITY;

      scrollContainer
        .querySelectorAll("[data-full-view-page-content]")
        .forEach((pageElement) => {
          const pageBounds = pageElement.getBoundingClientRect();
          const pageDistance = Math.abs(
            pageBounds.top + pageBounds.height / 2 - zoomAnchorClientY,
          );

          if (pageDistance < anchorPageDistance) {
            anchorPageElement = pageElement;
            anchorPageDistance = pageDistance;
          }
        });

      const anchorPageBounds = anchorPageElement.getBoundingClientRect();
      const horizontalPagePosition = Math.min(
        Math.max(
          (zoomAnchorClientX - anchorPageBounds.left) / anchorPageBounds.width,
          0,
        ),
        1,
      );
      const verticalPagePosition = Math.min(
        Math.max(
          (zoomAnchorClientY - anchorPageBounds.top) / anchorPageBounds.height,
          0,
        ),
        1,
      );

      flushSync(() => {
        setFullViewZoomPercentage((currentZoomPercentage) => {
          return Math.min(
            Math.max(currentZoomPercentage + zoomOffset, 50),
            300,
          );
        });
      });

      const resizedPageBounds = anchorPageElement.getBoundingClientRect();
      const resizedAnchorClientX =
        resizedPageBounds.left +
        resizedPageBounds.width * horizontalPagePosition;
      const resizedAnchorClientY =
        resizedPageBounds.top + resizedPageBounds.height * verticalPagePosition;

      scrollContainer.scrollLeft += resizedAnchorClientX - zoomAnchorClientX;
      scrollContainer.scrollTop += resizedAnchorClientY - zoomAnchorClientY;
    },
    [],
  );

  /**
   * Handles full-screen document wheel zoom.
   * @param {WheelEvent} event The wheel event.
   * @returns {void}
   */
  const handleFullViewWheel = useCallback(
    (event) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      event.preventDefault();
      changeFullViewZoom(
        event.deltaY < 0 ? 10 : -10,
        event.clientX,
        event.clientY,
      );
    },
    [changeFullViewZoom],
  );

  /**
   * Initializes the transitioned full-screen scroll element when it mounts.
   * @param {HTMLElement|null} scrollContainer The mounted scroll element.
   * @returns {void}
   */
  const handleFullViewScrollContainerReference = useCallback(
    (scrollContainer) => {
      if (fullViewInitialScrollAnimationFrameReference.current !== null) {
        window.cancelAnimationFrame(
          fullViewInitialScrollAnimationFrameReference.current,
        );
        fullViewInitialScrollAnimationFrameReference.current = null;
      }

      if (fullViewNavigationScrollEndTimeoutReference.current !== null) {
        window.clearTimeout(
          fullViewNavigationScrollEndTimeoutReference.current,
        );
        fullViewNavigationScrollEndTimeoutReference.current = null;
      }

      fullViewNavigationPageNumberReference.current = null;

      if (fullViewScrollReference.current) {
        fullViewScrollReference.current.removeEventListener(
          "wheel",
          handleFullViewWheel,
        );
      }

      fullViewScrollReference.current = scrollContainer;

      if (scrollContainer) {
        scrollContainer.addEventListener("wheel", handleFullViewWheel, {
          passive: false,
        });
        fullViewInitialScrollAnimationFrameReference.current =
          window.requestAnimationFrame(() => {
            const initialPageElement = scrollContainer.querySelector(
              `[data-full-view-page-number="${fullViewInitialPageNumberReference.current}"]`,
            );

            initialPageElement.scrollIntoView({ block: "start" });
            fullViewInitialScrollAnimationFrameReference.current = null;
          });
      }
    },
    [handleFullViewWheel],
  );

  /**
   * Scrolls the full-screen document to a page.
   * @param {number} pageNumber The destination page number.
   * @returns {void}
   */
  const navigateFullViewToPage = (pageNumber) => {
    const pageElement = fullViewScrollReference.current.querySelector(
      `[data-full-view-page-number="${pageNumber}"]`,
    );

    fullViewNavigationPageNumberReference.current = pageNumber;
    setSelectedPageNumber(pageNumber);
    pageElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  /**
   * Synchronizes the selected page with the page nearest the full-screen viewport center.
   * @returns {void}
   */
  const synchronizeSelectedFullViewPage = () => {
    const scrollContainerBounds =
      fullViewScrollReference.current.getBoundingClientRect();
    const viewportCenter =
      scrollContainerBounds.top + scrollContainerBounds.height / 2;
    let closestPageNumber = selectedPageNumber;
    let closestPageDistance = Number.POSITIVE_INFINITY;

    fullViewScrollReference.current
      .querySelectorAll("[data-full-view-page-number]")
      .forEach((pageElement) => {
        const pageBounds = pageElement.getBoundingClientRect();
        const pageDistance = Math.abs(
          pageBounds.top + pageBounds.height / 2 - viewportCenter,
        );

        if (pageDistance < closestPageDistance) {
          closestPageDistance = pageDistance;
          closestPageNumber = Number(pageElement.dataset.fullViewPageNumber);
        }
      });

    if (closestPageNumber !== selectedPageNumber) {
      setSelectedPageNumber(closestPageNumber);
    }
  };

  /**
   * Synchronizes page tracking after user scrolling or a completed animated page jump.
   * @returns {void}
   */
  const handleFullViewScroll = () => {
    if (fullViewNavigationPageNumberReference.current !== null) {
      if (fullViewNavigationScrollEndTimeoutReference.current !== null) {
        window.clearTimeout(
          fullViewNavigationScrollEndTimeoutReference.current,
        );
      }

      fullViewNavigationScrollEndTimeoutReference.current = window.setTimeout(
        () => {
          fullViewNavigationPageNumberReference.current = null;
          fullViewNavigationScrollEndTimeoutReference.current = null;
          synchronizeSelectedFullViewPage();
        },
        100,
      );
      return;
    }

    synchronizeSelectedFullViewPage();
  };

  // Registers keyboard shortcuts for page navigation and rotation.
  useEffect(() => {
    /**
     * Determines whether a keyboard event originated in an editable control.
     * @param {KeyboardEvent} event The keyboard event.
     * @returns {boolean} Whether the shortcut should be ignored.
     */
    const shouldIgnoreShortcut = (event) => {
      const eventTarget = event.target;

      if (!(eventTarget instanceof HTMLElement)) {
        return false;
      }

      const targetTagName = eventTarget.tagName.toLowerCase();

      return (
        eventTarget.isContentEditable ||
        targetTagName === "input" ||
        targetTagName === "select" ||
        targetTagName === "textarea"
      );
    };

    /**
     * Handles inbound-file preview keyboard shortcuts.
     * @param {KeyboardEvent} event The keyboard event.
     * @returns {void}
     */
    const handleKeyboardShortcut = (event) => {
      const hasZoomModifier = event.ctrlKey || event.metaKey;

      if (
        isFullViewOpen &&
        hasZoomModifier &&
        !event.altKey &&
        !shouldIgnoreShortcut(event)
      ) {
        if (event.key === "+" || event.key === "=") {
          event.preventDefault();
          changeFullViewZoom(25);
          return;
        }

        if (event.key === "-") {
          event.preventDefault();
          changeFullViewZoom(-25);
          return;
        }
      }

      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        shouldIgnoreShortcut(event) ||
        !inboundFileIdentifier
      ) {
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        if (isFullViewOpen) {
          return;
        }

        event.preventDefault();
        flushSync(() => moveSelectedPage(-1));
        return;
      }

      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        if (isFullViewOpen) {
          return;
        }

        event.preventDefault();
        flushSync(() => moveSelectedPage(1));
        return;
      }

      if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        flushSync(
          event.shiftKey ? handleRotateCounterClockwise : handleRotateClockwise,
        );
        return;
      }

      if (
        event.key.toLowerCase() === "f" &&
        !event.repeat &&
        pageNumbers.length > 0 &&
        !isPreviewUnavailable
      ) {
        event.preventDefault();
        flushSync(isFullViewOpen ? handleCloseFullView : handleOpenFullView);
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcut, true);

    return () => {
      window.removeEventListener("keydown", handleKeyboardShortcut, true);
    };
  }, [
    changeFullViewZoom,
    inboundFileIdentifier,
    isFullViewOpen,
    isPreviewUnavailable,
    pageNumbers,
    selectedPageNumber,
  ]);

  /**
   * Renders a page preview.
   * @param {number} pageNumber The page number.
   * @returns {React.ReactElement} The page preview.
   */
  const renderPage = (pageNumber) => {
    const rotationDegrees = rotationDegreesByPageNumber[pageNumber] || 0;
    const thumbnailSource = getThumbnailSource(pageNumber);

    return (
      <MUIPaper
        elevation={1}
        sx={(theme) => ({
          aspectRatio: "8.5 / 11",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
          flexShrink: 1,
          height: "calc(100% - 16px)",
          maxHeight: "calc(100% - 16px)",
          maxWidth: "100%",
          minHeight: 0,
          overflow: "hidden",
          left: "50%",
          position: "absolute",
          top: "50%",
          transform: `translate(-50%, -50%) rotate(${rotationDegrees}deg)`,
          transformOrigin: "center center",
          transition: "transform 160ms ease",
          width: "auto",
        })}
      >
        {thumbnailSource ? (
          <img
            src={thumbnailSource}
            alt={`Fax page ${pageNumber}`}
            style={{
              display: "block",
              height: "100%",
              objectFit: "contain",
              width: "100%",
            }}
          />
        ) : (
          <MUISkeleton height="100%" variant="rectangular" width="100%" />
        )}
      </MUIPaper>
    );
  };

  /**
   * Renders a page in the continuous full-screen document flow.
   * @param {number} pageNumber The page number.
   * @returns {React.ReactElement} The full-screen page.
   */
  const renderFullViewPage = (pageNumber) => {
    const rotationDegrees = rotationDegreesByPageNumber[pageNumber] || 0;
    const thumbnailSource = getThumbnailSource(pageNumber, fullViewPageWidth);
    const scaledViewportWidth = fullViewZoomPercentage;
    const scaledSidebarAndSpacingWidth = fullViewZoomPercentage * 2;
    const scaledMaximumPageWidth = fullViewZoomPercentage * 7.6;

    return (
      <MUIStack
        key={pageNumber}
        data-full-view-page-number={pageNumber}
        alignItems="center"
        sx={{
          flexShrink: 0,
          scrollMarginTop: 56,
          width: "100%",
        }}
      >
        <MUIPaper
          data-full-view-page-content={pageNumber}
          elevation={2}
          sx={(theme) => ({
            aspectRatio: "8.5 / 11",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxSizing: "border-box",
            flexShrink: 0,
            overflow: "hidden",
            transform: `rotate(${rotationDegrees}deg)`,
            transformOrigin: "center center",
            transition: "transform 160ms ease",
            width: `min(calc(${scaledViewportWidth}vw - ${scaledSidebarAndSpacingWidth}px), ${scaledMaximumPageWidth}px)`,
          })}
        >
          {thumbnailSource ? (
            <img
              src={thumbnailSource}
              alt={`Fax page ${pageNumber}`}
              style={{
                display: "block",
                height: "100%",
                objectFit: "contain",
                width: "100%",
              }}
            />
          ) : (
            <MUISkeleton height="100%" variant="rectangular" width="100%" />
          )}
        </MUIPaper>
      </MUIStack>
    );
  };

  /**
   * Handles keyboard selection of a page thumbnail.
   * @param {React.KeyboardEvent} event The keyboard event.
   * @param {number} pageNumber The page number.
   * @param {boolean} isFullView Whether the thumbnail is in the full-screen sidebar.
   * @returns {void}
   */
  const handleThumbnailKeyDown = (event, pageNumber, isFullView) => {
    if (
      event.target === event.currentTarget &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();

      if (isFullView) {
        navigateFullViewToPage(pageNumber);
        return;
      }

      setSelectedPageNumber(pageNumber);
    }
  };

  /**
   * Renders a selectable page thumbnail.
   * @param {number} pageNumber The page number.
   * @param {boolean} isFullView Whether the thumbnail is in the full-screen sidebar.
   * @returns {React.ReactElement} The page thumbnail.
   */
  const renderThumbnail = (pageNumber, isFullView = false) => {
    const thumbnailSource = getThumbnailSource(pageNumber);
    const isSelected = selectedPageNumber === pageNumber;
    const pageRotationDegrees = rotationDegreesByPageNumber[pageNumber] || 0;
    const rotationDegrees = ((pageRotationDegrees % 360) + 360) % 360;

    if (!thumbnailSource) {
      return (
        <MUISkeleton
          key={pageNumber}
          data-preview-page-number={pageNumber}
          height={isFullView ? 145 : 91}
          sx={{ borderRadius: 1, flexShrink: 0, width: isFullView ? 112 : 70 }}
          variant="rectangular"
        />
      );
    }

    return (
      <MUIStack
        key={pageNumber}
        component="button"
        type="button"
        data-preview-page-number={pageNumber}
        onClick={() =>
          isFullView
            ? navigateFullViewToPage(pageNumber)
            : setSelectedPageNumber(pageNumber)
        }
        onKeyDown={(event) =>
          handleThumbnailKeyDown(event, pageNumber, isFullView)
        }
        sx={(theme) => ({
          backgroundColor: "transparent",
          border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 1,
          color: theme.palette.text.primary,
          cursor: "pointer",
          flexShrink: 0,
          outline: "none",
          p: 0,
          position: "relative",
          textAlign: "left",
          width: isFullView ? 112 : 70,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        <MUIPaper
          elevation={0}
          sx={(theme) => ({
            aspectRatio: "8.5 / 11",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            position: "relative",
          })}
        >
          {rotationDegrees !== 0 ? (
            <MUIStack
              direction="row"
              alignItems="center"
              sx={(theme) => ({
                backgroundColor: theme.palette.primary.main,
                borderRadius: 999,
                bottom: 4,
                color: theme.palette.primary.contrastText,
                gap: 0.25,
                left: 4,
                px: 0.5,
                py: 0.25,
                position: "absolute",
                zIndex: 2,
              })}
            >
              <MUIRotateRightIcon sx={{ fontSize: 12 }} />
              <MUITypography
                variant="caption"
                sx={{ fontSize: 10, lineHeight: 1 }}
              >
                {rotationDegrees}°
              </MUITypography>
            </MUIStack>
          ) : null}
          <MUIChip
            label={`Page ${pageNumber}`}
            size="small"
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 999,
              color: theme.palette.text.secondary,
              height: 20,
              left: 6,
              position: "absolute",
              top: 6,
              zIndex: 2,
              ".MuiChip-label": {
                fontSize: 10,
                fontWeight: 600,
                px: 0.75,
              },
            })}
          />
          <img
            src={thumbnailSource}
            alt=""
            style={{
              display: "block",
              height: "100%",
              objectFit: "cover",
              width: "100%",
            }}
          />
        </MUIPaper>
      </MUIStack>
    );
  };

  /**
   * Renders an unavailable-preview message.
   * @returns {React.ReactElement} The unavailable state.
   */
  const renderUnavailableState = () => {
    let title = "Select a fax from the inbox to preview";
    let detail = null;
    let StateIcon = MUIWarningAmberIcon;

    if (isPreviewUnavailable && !props.inboundFile) {
      title = "Select a file first";
    } else if (props.isWaitingForProcessing) {
      title = "This file has not been processed yet.";
      detail = "Previews will be available once the file is processed.";
      StateIcon = MUIHourglassEmptyIcon;
    } else if (isPreviewUnavailable) {
      title = "Preview Unavailable";
      detail = props.linkedOrderIdentifier ? (
        <MUILink
          component="button"
          variant="caption"
          onClick={() => {
            // window.open (not an <a href target="_blank">) so the new tab is
            // script-opened and inherits sessionStorage, which holds the auth
            // session guid the file viewer needs to fetch the file.
            window.open(
              `/file/${props.inboundFile.uploadedFileId}/${props.linkedOrderIdentifier}-${props.inboundFile.uploadedFileId}.pdf`,
              "_blank",
            );
          }}
        >
          View Attached Fax
        </MUILink>
      ) : null;
    } else if (props.inboundFile && pageNumbers.length === 0) {
      title = "No preview pages available for this fax";
    }

    return (
      <MUIStack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={(theme) => ({
          backgroundColor: theme.palette.action.hover,
          height: "100%",
          width: "100%",
        })}
      >
        <StateIcon color="disabled" />
        <MUITypography
          variant="body2"
          color="text.secondary"
          textAlign="center"
        >
          {title}
        </MUITypography>
        {detail ? (
          typeof detail === "string" ? (
            <MUITypography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {detail}
            </MUITypography>
          ) : (
            detail
          )
        ) : null}
      </MUIStack>
    );
  };

  const shouldShowUnavailableState =
    !props.inboundFile ||
    props.isWaitingForProcessing ||
    isPreviewUnavailable ||
    pageNumbers.length === 0;

  if (shouldShowUnavailableState) {
    return renderUnavailableState();
  }

  return (
    <>
      <MUIStack sx={{ height: "100%", minHeight: 0, width: "100%" }}>
        <MUIStack
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            backgroundColor: theme.palette.action.hover,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
          })}
        >
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{
              left: 8,
              pointerEvents: "none",
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 2,
              "& > *": {
                pointerEvents: "auto",
              },
            }}
          >
            <MUIChip
              label={`Page ${selectedPageNumber} of ${pageNumbers.length}`}
              size="small"
            />
            <MUIStack direction="row" spacing={0.75}>
              <MUIIconButton
                title="Rotate 90 degrees [R]"
                size="small"
                onClick={handleRotateClockwise}
              >
                <MUIRotateRightIcon fontSize="small" />
              </MUIIconButton>
              <MUIIconButton
                title="Full screen [F]"
                size="small"
                onClick={handleOpenFullView}
              >
                <MUIOpenInFullIcon fontSize="small" />
              </MUIIconButton>
            </MUIStack>
          </MUIStack>
          {renderPage(selectedPageNumber)}
        </MUIStack>
        <MUIDivider />
        <MUIStack
          ref={thumbnailStripReference}
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0,
            height: 116,
            overflowX: "scroll",
            overflowY: "hidden",
            px: 1.25,
            py: 0.25,
          })}
        >
          {pageNumbers.map((pageNumber) => {
            return renderThumbnail(pageNumber);
          })}
        </MUIStack>
      </MUIStack>
      <MUIDialog
        fullScreen={true}
        open={isFullViewOpen}
        onClose={handleCloseFullView}
      >
        <MUIDialogContent
          sx={(theme) => ({
            backgroundColor: theme.palette.action.hover,
            height: "100dvh",
            overflow: "hidden",
            p: 0,
            position: "relative",
          })}
        >
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{
              left: 144,
              pointerEvents: "none",
              position: "absolute",
              right: 16,
              top: 8,
              zIndex: 2,
              "& > *": {
                pointerEvents: "auto",
              },
            }}
          >
            <MUIChip
              label={`Page ${selectedPageNumber} of ${pageNumbers.length}`}
              size="small"
            />
            <MUIStack direction="row" alignItems="center" spacing={0.75}>
              <MUIIconButton
                title="Zoom out"
                size="small"
                disabled={fullViewZoomPercentage === 50}
                onClick={() => changeFullViewZoom(-25)}
              >
                <MUIRemoveIcon fontSize="small" />
              </MUIIconButton>
              <MUIChip label={`${fullViewZoomPercentage}%`} size="small" />
              <MUIIconButton
                title="Zoom in"
                size="small"
                disabled={fullViewZoomPercentage === 300}
                onClick={() => changeFullViewZoom(25)}
              >
                <MUIAddIcon fontSize="small" />
              </MUIIconButton>
              <MUIIconButton
                title="Rotate 90 degrees [R]"
                size="small"
                onClick={handleRotateClockwise}
              >
                <MUIRotateRightIcon fontSize="small" />
              </MUIIconButton>
              <MUIIconButton
                title="Close full screen [F / Esc]"
                size="small"
                onClick={handleCloseFullView}
              >
                <MUICloseIcon fontSize="small" />
              </MUIIconButton>
            </MUIStack>
          </MUIStack>
          <MUIStack
            direction="row"
            sx={{ height: "100dvh", minHeight: 0, minWidth: 0, width: "100vw" }}
          >
            <MUIStack
              ref={fullViewThumbnailSidebarReference}
              alignItems="center"
              spacing={1}
              sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                borderRight: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
                overflowX: "hidden",
                overflowY: "auto",
                pb: 2,
                pt: 2,
                scrollbarGutter: "stable",
                width: 136,
              })}
            >
              {pageNumbers.map((pageNumber) => {
                return renderThumbnail(pageNumber, true);
              })}
            </MUIStack>
            <MUIStack
              ref={handleFullViewScrollContainerReference}
              onScroll={handleFullViewScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: "auto",
                pb: 3,
                pt: 7,
                scrollbarGutter: "stable",
              }}
            >
              <MUIStack
                alignItems="center"
                spacing={2.5}
                sx={{ minWidth: "max-content" }}
              >
                {pageNumbers.map((pageNumber) => {
                  return renderFullViewPage(pageNumber);
                })}
              </MUIStack>
            </MUIStack>
          </MUIStack>
        </MUIDialogContent>
      </MUIDialog>
    </>
  );
}

/**
 * Renders the inbound-files workspace.
 * @param {object} props Component properties.
 * @param {object} props.activeWidget The active dashboard widget.
 * @param {array} props.dashboardWidgets The dashboard widgets.
 * @param {function} props.onActiveWidgetChange Handles changing the active widget.
 * @param {function} props.updateActiveWidgetConfiguration Updates the active widget configuration.
 * @returns {React.ReactElement} The inbound-files workspace.
 */
export default function InboundFiles(props) {
  const mainContext = useContext(MainContext);
  const initializedRecipientContactsUserIdReference = useRef(null);
  const inboundFileRecordsRequestIdReference = useRef(0);
  const openOrderRecordsRequestIdReference = useRef(0);
  const orderLookupRequestIdReference = useRef(0);
  const openOrderPollTimeoutReference = useRef(null);
  const openOrderPollingSessionIdReference = useRef(0);
  const isOpenOrderPollingActiveReference = useRef(false);
  const isInboundFilesMountedReference = useRef(true);
  const [selectedInboundFileIdentifier, setSelectedInboundFileIdentifier] =
    useState(null);
  const [selectedOrderIdentifier, setSelectedOrderIdentifier] = useState("");
  const [orderIdentifierInputText, setOrderIdentifierInputText] = useState("");
  const [selectedActivityFileType, setSelectedActivityFileType] = useState("");
  const [activityFileTypeInputText, setActivityFileTypeInputText] =
    useState("");
  const [
    activityFileTypeAutocompleteResetKey,
    setActivityFileTypeAutocompleteResetKey,
  ] = useState(0);
  const [otherDocumentFileName, setOtherDocumentFileName] = useState("");
  const [
    selectedInboundFileSectionIdentifier,
    setSelectedInboundFileSectionIdentifier,
  ] = useState(InboundFilesConfiguration.sectionIds.inbox);
  const [allInboundFileRecords, setAllInboundFileRecords] = useState([]);
  const [inboundFileSearchText, setInboundFileSearchText] = useState("");
  const [selectedRecipientContacts, setSelectedRecipientContacts] = useState(
    [],
  );
  const [isInboundFileListLoading, setIsInboundFileListLoading] =
    useState(true);
  const [openOrderRecords, setOpenOrderRecords] = useState([]);
  const [searchedOpenOrderOption, setSearchedOpenOrderOption] = useState(null);
  const [isOpenOrderListLoading, setIsOpenOrderListLoading] = useState(false);
  const [isOrderLookupLoading, setIsOrderLookupLoading] = useState(false);
  const [isAttachingInboundFile, setIsAttachingInboundFile] = useState(false);
  const [isDeletingInboundFile, setIsDeletingInboundFile] = useState(false);
  const [isTransferringInboundFile, setIsTransferringInboundFile] =
    useState(false);
  const [isRestoringInboundFile, setIsRestoringInboundFile] = useState(false);
  const [isDeleteInboundFileDialogOpen, setIsDeleteInboundFileDialogOpen] =
    useState(false);
  const [isTransferInboundFileDialogOpen, setIsTransferInboundFileDialogOpen] =
    useState(false);
  const [selectedAttachmentPages, setSelectedAttachmentPages] = useState([]);
  const [isInboundFileRefreshLoading, setIsInboundFileRefreshLoading] =
    useState(false);
  const isDashboardRoute = Dashboard.isDashboardRoute();

  /**
   * Gets the inbound file records by section identifier.
   * @returns {object} The inbound file records by section identifier.
   */
  const inboundFileRecordsBySectionIdentifier = useMemo(() => {
    const recordsBySectionIdentifier = {};
    for (const inboundFileSectionConfig of InboundFilesConfiguration.sections) {
      const filteredRecords = allInboundFileRecords.filter(
        (inboundFileRecord) => {
          return (
            getInboundFileFolder(inboundFileRecord) ===
            inboundFileSectionConfig.id
          );
        },
      );

      recordsBySectionIdentifier[inboundFileSectionConfig.id] =
        InboundFile.sort(
          filteredRecords,
          inboundFileSectionConfig.id ===
            InboundFilesConfiguration.sectionIds.deleted
            ? "deletedAt"
            : "createdAt",
        );
    }
    return recordsBySectionIdentifier;
  }, [allInboundFileRecords]);

  /**
   * Gets the inbound file records for the selected section.
   * @returns {array} The inbound file records for the selected section.
   */
  const inboundFileRecords =
    inboundFileRecordsBySectionIdentifier[
      selectedInboundFileSectionIdentifier
    ] || [];

  /**
   * Gets the users that should be available as recipient options.
   * @returns {array} The recipient option users.
   */
  const recipientOptionUsers = useMemo(() => {
    const currentUser = mainContext.currentUser;
    const filteredUsers = User.getFilteredUsers(
      currentUser,
      mainContext.userprofileLocationsIndexed,
      mainContext.locationsIndexed,
      mainContext.usersIndexed,
      mainContext.salesPcrLinksIndexed,
    );
    const recipientOptionUserById = new Map(
      filteredUsers.map((user) => [user.id, user]),
    );

    // A user should always retain access to their own faxes, even if their
    // profile would otherwise be excluded from the dashboard prefilter.
    recipientOptionUserById.set(currentUser.id, currentUser);

    return User.sort(
      [...recipientOptionUserById.values()].filter((user) => {
        return Format.phone(user.fax);
      }),
    );
  }, [
    mainContext.currentUser,
    mainContext.locationsIndexed,
    mainContext.salesPcrLinksIndexed,
    mainContext.userprofileLocationsIndexed,
    mainContext.usersIndexed,
  ]);

  /**
   * Gets the user IDs represented by the recipient dropdown.
   * @returns {array} The recipient user profile IDs.
   */
  const recipientUserProfileIds = useMemo(() => {
    return recipientOptionUsers.map((user) => user.id).filter(Boolean);
  }, [recipientOptionUsers]);

  /**
   * Gets the users that can receive a transferred fax.
   * @returns {array} The transfer user options.
   */
  const transferUserOptions = useMemo(() => {
    return User.sort(
      Object.values(mainContext.usersIndexed).filter((user) => {
        return user.active === true && Format.phone(user.fax);
      }),
    ).map((user) => {
      const locationNames = Object.values(
        mainContext.userprofileLocationsIndexed,
      )
        .filter((userprofileLocation) => {
          return userprofileLocation.userprofileId === user.id;
        })
        .map((userprofileLocation) => {
          return mainContext.locationsIndexed[userprofileLocation.locationsId]
            .name;
        })
        .sort();

      return {
        locationNames: locationNames,
        phoneSearchText: Format.phone(user.fax).replace(/\D/g, ""),
        searchText: [
          User.getDisplayName(user),
          user.fax,
          Format.phone(user.fax),
          ...locationNames,
        ]
          .join(" ")
          .toLowerCase(),
        user: user,
      };
    });
  }, [
    mainContext.allUsers,
    mainContext.locationsIndexed,
    mainContext.userprofileLocationsIndexed,
  ]);

  /**
   * Loads inbound file records from the API.
   * @param {object} options - Load options.
   * @param {boolean} options.shouldResetSelection - Whether to reset the selected file and preview state.
   * @param {boolean} options.shouldShowListLoading - Whether to show the full list loading state.
   * @param {boolean} options.shouldShowRefreshLoading - Whether to show the refresh button loading state.
   * @param {boolean} options.shouldShowSuccessSnackbar - Whether to show a success snackbar.
   */
  const loadInboundFileRecords = useCallback(
    async (loadOptions = {}) => {
      const shouldResetSelection = Boolean(loadOptions.shouldResetSelection);
      const shouldShowListLoading = Boolean(loadOptions.shouldShowListLoading);
      const shouldShowRefreshLoading = Boolean(
        loadOptions.shouldShowRefreshLoading,
      );
      const shouldShowSuccessSnackbar = Boolean(
        loadOptions.shouldShowSuccessSnackbar,
      );
      if (recipientUserProfileIds.length === 0) {
        setAllInboundFileRecords([]);
        setIsInboundFileListLoading(false);
        setIsInboundFileRefreshLoading(false);
        return;
      }

      const requestId = inboundFileRecordsRequestIdReference.current + 1;
      inboundFileRecordsRequestIdReference.current = requestId;

      if (shouldShowListLoading) {
        setIsInboundFileListLoading(true);
      }

      if (shouldShowRefreshLoading) {
        setIsInboundFileRefreshLoading(true);
      }

      const payload = {
        userprofileids: recipientUserProfileIds,
      };

      if (shouldResetSelection) {
        setSelectedInboundFileIdentifier(null);
        setSelectedAttachmentPages([]);
      }

      try {
        const inboundFileRecordsForSection = await api.send(
          "POST",
          "/inboundFile/getActiveInboundFiles",
          payload,
        );

        if (
          !isInboundFilesMountedReference.current ||
          requestId !== inboundFileRecordsRequestIdReference.current
        ) {
          return;
        }

        if (!Array.isArray(inboundFileRecordsForSection)) {
          throw new Error("The inbound file response must be an array.");
        }

        setAllInboundFileRecords(inboundFileRecordsForSection);

        if (shouldShowSuccessSnackbar) {
          NotistackEnqueueSnackbar("Faxes refreshed", { variant: "success" });
        }
      } catch {
        if (shouldShowSuccessSnackbar) {
          NotistackEnqueueSnackbar("Failed to refresh faxes", {
            variant: "error",
          });
        }
      } finally {
        if (
          isInboundFilesMountedReference.current &&
          requestId === inboundFileRecordsRequestIdReference.current
        ) {
          if (shouldShowListLoading) {
            setIsInboundFileListLoading(false);
          }

          if (shouldShowRefreshLoading) {
            setIsInboundFileRefreshLoading(false);
          }
        }
      }
    },
    [recipientUserProfileIds],
  );

  /**
   * Loads open order records from the API.
   * @param {number|string} recipientUserProfileId - The selected inbound file recipient user profile ID.
   * @param {object} loadOptions - Load options.
   * @param {boolean} loadOptions.shouldShowLoading - Whether to clear the list and show the loading state
   * while this request is in flight. Pass false for background polling so results swap in silently.
   * @returns {Promise<void>}
   */
  const loadOpenOrderRecords = useCallback(
    async (recipientUserProfileId, loadOptions = {}) => {
      const shouldShowLoading = loadOptions.shouldShowLoading !== false;
      const requestId = openOrderRecordsRequestIdReference.current + 1;
      openOrderRecordsRequestIdReference.current = requestId;

      if (shouldShowLoading) {
        setOpenOrderRecords([]);
        setIsOpenOrderListLoading(true);
      }

      const payload = {
        userprofileids: [recipientUserProfileId],
      };

      try {
        const openOrders = await api.send(
          "POST",
          "/inboundFile/getOpenOrders",
          payload,
        );

        if (
          !isInboundFilesMountedReference.current ||
          requestId !== openOrderRecordsRequestIdReference.current
        ) {
          return;
        }

        setOpenOrderRecords(
          InboundFilesConfiguration.getOpenOrderRecordsFromResponse(openOrders),
        );
      } catch {
        if (shouldShowLoading) {
          NotistackEnqueueSnackbar("Failed to load open orders", {
            variant: "error",
          });
        }
      } finally {
        if (
          isInboundFilesMountedReference.current &&
          requestId === openOrderRecordsRequestIdReference.current
        ) {
          setIsOpenOrderListLoading(false);
        }
      }
    },
    [],
  );

  /**
   * Stops any in-progress open-order polling.
   * @returns {void}
   */
  const stopOpenOrderPolling = useCallback(() => {
    isOpenOrderPollingActiveReference.current = false;
    openOrderPollingSessionIdReference.current += 1;

    if (openOrderPollTimeoutReference.current !== null) {
      window.clearTimeout(openOrderPollTimeoutReference.current);
      openOrderPollTimeoutReference.current = null;
    }
  }, []);

  /**
   * Polls open orders for the given recipient every few seconds so an order created in another tab
   * (e.g. via "Create New Patient") shows up without a manual refresh. Runs only while the Order #
   * field has focus; see stopOpenOrderPolling.
   * @param {number|string} recipientUserProfileId - The recipient user profile ID to poll for.
   * @returns {void}
   */
  const startOpenOrderPolling = useCallback(
    (recipientUserProfileId) => {
      if (
        !recipientUserProfileId ||
        isOpenOrderPollingActiveReference.current
      ) {
        return;
      }

      isOpenOrderPollingActiveReference.current = true;
      const pollingSessionId = openOrderPollingSessionIdReference.current + 1;
      openOrderPollingSessionIdReference.current = pollingSessionId;

      /**
       * Loads open orders and schedules the next poll while this polling session remains current.
       * @returns {Promise<void>}
       */
      const pollOpenOrderRecords = async () => {
        await loadOpenOrderRecords(recipientUserProfileId, {
          shouldShowLoading: false,
        });

        if (
          !isOpenOrderPollingActiveReference.current ||
          pollingSessionId !== openOrderPollingSessionIdReference.current
        ) {
          return;
        }

        openOrderPollTimeoutReference.current = window.setTimeout(
          pollOpenOrderRecords,
          InboundFilesConfiguration.openOrderPollIntervalMilliseconds,
        );
      };

      pollOpenOrderRecords();
    },
    [loadOpenOrderRecords],
  );

  useEffect(() => {
    isInboundFilesMountedReference.current = true;

    return () => {
      isInboundFilesMountedReference.current = false;
      stopOpenOrderPolling();
    };
  }, [stopOpenOrderPolling]);

  // Loads the inbound file records from the API.
  useEffect(() => {
    loadInboundFileRecords({
      shouldResetSelection: true,
      shouldShowListLoading: true,
    });
  }, [loadInboundFileRecords]);

  // Refreshes inbound file records in the background without disturbing the current workflow.
  useEffect(() => {
    const autoRefreshIntervalIdentifier = window.setInterval(() => {
      loadInboundFileRecords();
    }, InboundFilesConfiguration.autoRefreshIntervalMilliseconds);

    return () => {
      window.clearInterval(autoRefreshIntervalIdentifier);
    };
  }, [loadInboundFileRecords]);

  /**
   * Gets the recipient contact options for the inbound file records.
   * @returns {array} The recipient contact options.
   */
  const recipientContactOptions = useMemo(() => {
    const inboxInboundFileRecords =
      inboundFileRecordsBySectionIdentifier[
        InboundFilesConfiguration.sectionIds.inbox
      ] || [];
    const fileCountByRecipientContact = inboxInboundFileRecords.reduce(
      (countByRecipientContact, inboundFileRecord) => {
        const recipientContact = Format.phone(
          inboundFileRecord.recipientContact,
        );

        if (recipientContact) {
          countByRecipientContact[recipientContact] =
            (countByRecipientContact[recipientContact] || 0) + 1;
        }

        return countByRecipientContact;
      },
      {},
    );

    return recipientOptionUsers.map((user) => {
      const recipientContact = Format.phone(user.fax);
      const fileCount = fileCountByRecipientContact[recipientContact] || 0;

      return {
        count: fileCount,
        userProfileId: user.id,
        label: `${User.getDisplayName(user)} • ${Format.phone(user.fax)}`,
        value: recipientContact,
      };
    });
  }, [inboundFileRecordsBySectionIdentifier, recipientOptionUsers]);

  /**
   * Gets the recipient contact option by value.
   * @returns {object} The recipient contact option by value.
   */
  const recipientContactOptionByValue = useMemo(() => {
    return recipientContactOptions.reduce(
      (optionByValue, recipientContactOption) => {
        optionByValue[recipientContactOption.value] = recipientContactOption;
        return optionByValue;
      },
      {},
    );
  }, [recipientContactOptions]);

  /**
   * Gets the default recipient contacts for the current user.
   * @returns {array} The default recipient contacts.
   */
  const defaultRecipientContacts = useMemo(() => {
    const currentUserRecipientOption = recipientContactOptions.find(
      (recipientContactOption) => {
        return (
          recipientContactOption.userProfileId === mainContext.currentUser.id
        );
      },
    );

    return currentUserRecipientOption ? [currentUserRecipientOption.value] : [];
  }, [mainContext.currentUser.id, recipientContactOptions]);

  /**
   * Sets the selected recipient contacts.
   */
  useEffect(() => {
    const currentUserId = mainContext.currentUser.id;

    if (recipientContactOptions.length === 0) {
      if (selectedRecipientContacts.length > 0) {
        setSelectedRecipientContacts([]);
      }
      initializedRecipientContactsUserIdReference.current = null;
      setSelectedInboundFileIdentifier(null);
      setSelectedAttachmentPages([]);
      return;
    }

    const recipientContactOptionValueSet = new Set(
      recipientContactOptions.map(
        (recipientContactOption) => recipientContactOption.value,
      ),
    );
    const validSelectedRecipientContacts = selectedRecipientContacts.filter(
      (selectedRecipientContact) => {
        return recipientContactOptionValueSet.has(selectedRecipientContact);
      },
    );

    if (initializedRecipientContactsUserIdReference.current !== currentUserId) {
      const savedSelectedRecipientContacts = Setting.get(
        "inboundFiles.recipient",
      );
      if (Array.isArray(savedSelectedRecipientContacts)) {
        const validSavedSelectedRecipientContacts =
          savedSelectedRecipientContacts.filter((selectedRecipientContact) => {
            return recipientContactOptionValueSet.has(selectedRecipientContact);
          });

        initializedRecipientContactsUserIdReference.current = currentUserId;
        setSelectedRecipientContacts(
          validSavedSelectedRecipientContacts.length > 0
            ? validSavedSelectedRecipientContacts
            : defaultRecipientContacts,
        );
        return;
      }

      initializedRecipientContactsUserIdReference.current = currentUserId;
      setSelectedRecipientContacts(defaultRecipientContacts);
      return;
    }

    if (
      validSelectedRecipientContacts.length !== selectedRecipientContacts.length
    ) {
      Setting.set("inboundFiles.recipient", validSelectedRecipientContacts);
      setSelectedRecipientContacts(validSelectedRecipientContacts);
    }
  }, [
    defaultRecipientContacts,
    mainContext.currentUser.id,
    recipientContactOptions,
    selectedRecipientContacts,
  ]);

  /**
   * Gets the filtered inbound file records.
   * @returns {array} The filtered inbound file records.
   */
  const filteredInboundFileRecords = useMemo(() => {
    if (
      recipientContactOptions.length === 0 ||
      selectedRecipientContacts.length === 0
    ) {
      return [];
    }

    const normalizedInboundFileSearchText = inboundFileSearchText
      .trim()
      .toLowerCase();
    const normalizedPhoneSearchText = Format.phone(
      inboundFileSearchText,
    ).replace(/\D/g, "");
    const selectedRecipientContactSet = new Set(selectedRecipientContacts);
    const recipientContactFilteredInboundFileRecords =
      inboundFileRecords.filter((inboundFileRecord) => {
        return selectedRecipientContactSet.has(
          Format.phone(inboundFileRecord.recipientContact),
        );
      });

    if (normalizedInboundFileSearchText === "") {
      return recipientContactFilteredInboundFileRecords;
    }

    return recipientContactFilteredInboundFileRecords.filter(
      (inboundFileRecord) => {
        const inboundFileType =
          mainContext.termsIndexed[inboundFileRecord.inboundFileTypeTermId];
        const recipientContactOption =
          recipientContactOptionByValue[
            Format.phone(inboundFileRecord.recipientContact)
          ];
        const searchableInboundFileText = [
          inboundFileType ? inboundFileType.name : "",
          InboundFile.getDisplayName(inboundFileRecord),
          recipientContactOption ? recipientContactOption.label : "",
          getInboundFileDetailText(inboundFileRecord),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const searchableInboundFilePhoneText = [
          inboundFileRecord.senderContact,
          inboundFileRecord.recipientContact,
        ]
          .map((phoneNumber) => Format.phone(phoneNumber).replace(/\D/g, ""))
          .filter(Boolean)
          .join(" ");

        return (
          searchableInboundFileText.includes(normalizedInboundFileSearchText) ||
          (normalizedPhoneSearchText &&
            searchableInboundFilePhoneText.includes(normalizedPhoneSearchText))
        );
      },
    );
  }, [
    inboundFileRecords,
    inboundFileSearchText,
    mainContext.termsIndexed,
    recipientContactOptionByValue,
    recipientContactOptions.length,
    selectedInboundFileSectionIdentifier,
    selectedRecipientContacts,
  ]);

  /**
   * Gets the selected inbound file record.
   * @returns {object} The selected inbound file record.
   */
  const selectedInboundFileRecord = useMemo(() => {
    return inboundFileRecords.find((inboundFileRecord) => {
      return inboundFileRecord.id === selectedInboundFileIdentifier;
    });
  }, [inboundFileRecords, selectedInboundFileIdentifier]);
  const selectedInboundFileRecipientOption = selectedInboundFileRecord
    ? recipientContactOptionByValue[
        Format.phone(selectedInboundFileRecord.recipientContact)
      ]
    : null;
  const selectedInboundFileRecipientUserProfileId =
    selectedInboundFileRecipientOption
      ? selectedInboundFileRecipientOption.userProfileId
      : null;

  // Loads open order options for the recipient of each opened inbound file.
  useEffect(() => {
    stopOpenOrderPolling();
    orderLookupRequestIdReference.current += 1;
    setSearchedOpenOrderOption(null);
    setIsOrderLookupLoading(false);

    if (!selectedInboundFileRecipientUserProfileId) {
      openOrderRecordsRequestIdReference.current += 1;
      setOpenOrderRecords([]);
      setIsOpenOrderListLoading(false);
      return;
    }

    loadOpenOrderRecords(selectedInboundFileRecipientUserProfileId);
  }, [
    loadOpenOrderRecords,
    selectedInboundFileIdentifier,
    selectedInboundFileRecipientUserProfileId,
    stopOpenOrderPolling,
  ]);

  const isSelectedInboundFileListItemDisabled = selectedInboundFileRecord
    ? isInboundFileWaitingForProcessing(selectedInboundFileRecord)
    : false;
  const isSelectedInboundFileSectionLinked =
    selectedInboundFileSectionIdentifier ===
    InboundFilesConfiguration.sectionIds.linked;
  const isSelectedInboundFileSectionInbox =
    selectedInboundFileSectionIdentifier ===
    InboundFilesConfiguration.sectionIds.inbox;
  const isSelectedInboundFileSectionDeleted =
    selectedInboundFileSectionIdentifier ===
    InboundFilesConfiguration.sectionIds.deleted;
  const isSelectedInboundFileSectionReadOnly =
    isSelectedInboundFileSectionLinked || isSelectedInboundFileSectionDeleted;
  const selectedInboundFileSectionLabel = isSelectedInboundFileSectionLinked
    ? "linked"
    : "deleted";
  // these should always be here now, until the actual file on drive is deleted, in which case they wont show up in this list anyways
  const isSelectedInboundFilePreviewUnavailable = false;
  const selectedLinkedOrderIdentifier =
    isSelectedInboundFileSectionLinked && selectedInboundFileRecord
      ? selectedInboundFileRecord.orderActivityId
      : "";

  /**
   * Gets the selected activity file type name.
   * @returns {string} The selected activity file type name.
   */
  const selectedActivityFileTypeName = useMemo(() => {
    if (Number(selectedActivityFileType) === 149) {
      return otherDocumentFileName.trim();
    }

    const selectedActivityFileTypeTerm =
      mainContext.termsIndexed[selectedActivityFileType];

    return selectedActivityFileTypeTerm
      ? selectedActivityFileTypeTerm.name
      : "";
  }, [
    mainContext.termsIndexed,
    otherDocumentFileName,
    selectedActivityFileType,
  ]);

  /**
   * Gets the activity file type options.
   * @returns {array} The activity file type options.
   */
  const activityFileTypeOptions = useMemo(() => {
    const activityFileTypeTerms = Object.values(
      mainContext.termsIndexed,
    ).filter((term) => {
      return term.type === "activity_file_type";
    });

    return ActivityFileType.sort(activityFileTypeTerms).map(
      (activityFileType) => {
        return {
          label: activityFileType.name,
          value: activityFileType.id,
        };
      },
    );
  }, [mainContext.termsIndexed]);

  const selectedActivityFileTypeOption =
    activityFileTypeOptions.find((activityFileTypeOption) => {
      return activityFileTypeOption.value === selectedActivityFileType;
    }) || null;

  /**
   * Gets the open-order autocomplete options.
   * @returns {array} The open-order options.
   */
  const openOrderOptions = useMemo(() => {
    const usedOpenOrderIdentifiers = new Set();

    return [
      ...openOrderRecords.map((openOrderRecord) => {
        return InboundFilesConfiguration.getOpenOrderOption(
          openOrderRecord,
          mainContext.orderStatusesIndexed,
          mainContext.orderStatusReasonsIndexed,
        );
      }),
      searchedOpenOrderOption,
    ]
      .filter((openOrderOption) => {
        if (
          !openOrderOption ||
          usedOpenOrderIdentifiers.has(openOrderOption.value)
        ) {
          return false;
        }

        usedOpenOrderIdentifiers.add(openOrderOption.value);
        return true;
      })
      .sort((firstOpenOrderOption, secondOpenOrderOption) => {
        return firstOpenOrderOption.label.localeCompare(
          secondOpenOrderOption.label,
        );
      });
  }, [
    mainContext.orderStatusReasonsIndexed,
    mainContext.orderStatusesIndexed,
    openOrderRecords,
    searchedOpenOrderOption,
  ]);

  /**
   * Gets the currently selected open-order option.
   * @returns {object|null} The selected option.
   */
  const selectedOpenOrderOption = useMemo(() => {
    return (
      openOrderOptions.find((openOrderOption) => {
        return openOrderOption.value === selectedOrderIdentifier.trim();
      }) || null
    );
  }, [openOrderOptions, selectedOrderIdentifier]);
  const selectedOpenOrderPatientIdentifier =
    selectedOpenOrderOption &&
    typeof selectedOpenOrderOption.record === "object"
      ? (selectedOpenOrderOption.record.patientId ??
        selectedOpenOrderOption.record.patientsId ??
        selectedOpenOrderOption.record.patient_id ??
        (selectedOpenOrderOption.record.patient
          ? selectedOpenOrderOption.record.patient.id
          : null))
      : null;

  /**
   * Looks up an order through the app-bar search and selects its structured option.
   * @param {string} orderIdentifier The order identifier to look up.
   * @returns {Promise<void>}
   */
  const handleOrderLookup = async (orderIdentifier) => {
    const requestId = orderLookupRequestIdReference.current + 1;
    orderLookupRequestIdReference.current = requestId;
    setSelectedOrderIdentifier("");
    setIsOrderLookupLoading(true);

    try {
      const response = await api.send("POST", "/search/omniSearch", {
        omniSearchTerm: orderIdentifier,
      });
      const matchingOrder =
        response && Array.isArray(response.orders)
          ? response.orders.find(
              (order) => String(order.id) === orderIdentifier,
            )
          : null;

      if (
        !isInboundFilesMountedReference.current ||
        requestId !== orderLookupRequestIdReference.current
      ) {
        return;
      }

      if (!matchingOrder) {
        NotistackEnqueueSnackbar(`Order #${orderIdentifier} was not found`, {
          variant: "warning",
        });
        return;
      }

      const matchingPatient = Array.isArray(response.patients)
        ? response.patients.find(
            (patient) => String(patient.id) === String(matchingOrder.patientId),
          )
        : null;
      const openOrderOption = InboundFilesConfiguration.getOpenOrderOption(
        matchingPatient
          ? { ...matchingOrder, patient: matchingPatient }
          : matchingOrder,
        mainContext.orderStatusesIndexed,
        mainContext.orderStatusReasonsIndexed,
      );

      setSearchedOpenOrderOption(openOrderOption);
      setSelectedOrderIdentifier(openOrderOption.value);
      setOrderIdentifierInputText(
        openOrderOption.inputLabel || openOrderOption.label,
      );
    } catch {
      if (
        isInboundFilesMountedReference.current &&
        requestId === orderLookupRequestIdReference.current
      ) {
        NotistackEnqueueSnackbar(
          `Failed to search for Order #${orderIdentifier}`,
          {
            variant: "error",
          },
        );
      }
    } finally {
      if (
        isInboundFilesMountedReference.current &&
        requestId === orderLookupRequestIdReference.current
      ) {
        setIsOrderLookupLoading(false);
      }
    }
  };

  /**
   * Opens a linked order in a new tab after resolving its patient identifier.
   * @param {string|number} orderIdentifier The linked order identifier.
   * @returns {Promise<void>}
   */
  const handleOpenLinkedOrder = async (orderIdentifier) => {
    const orderWindow = window.open("about:blank", "_blank");

    if (orderWindow) {
      orderWindow.opener = null;
    }

    try {
      const response = await api.send("POST", "/search/omniSearch", {
        omniSearchTerm: String(orderIdentifier),
      });
      const matchingOrder =
        response && Array.isArray(response.orders)
          ? response.orders.find(
              (order) => String(order.id) === String(orderIdentifier),
            )
          : null;

      if (!matchingOrder) {
        if (orderWindow) {
          orderWindow.close();
        }
        NotistackEnqueueSnackbar(`Order #${orderIdentifier} was not found`, {
          variant: "warning",
        });
        return;
      }

      const orderPath = `/chart/${matchingOrder.patientId}/order/${matchingOrder.id}`;

      if (orderWindow) {
        orderWindow.location.replace(orderPath);
        return;
      }

      window.open(orderPath, "_blank", "noopener,noreferrer");
    } catch {
      if (orderWindow) {
        orderWindow.close();
      }
      NotistackEnqueueSnackbar(`Failed to open Order #${orderIdentifier}`, {
        variant: "error",
      });
    }
  };

  /**
   * Selects the inbound file record.
   * @param {number} inboundFileIdentifier - The inbound file identifier.
   */
  const selectInboundFileRecord = useCallback((inboundFileIdentifier) => {
    orderLookupRequestIdReference.current += 1;
    setSelectedInboundFileIdentifier(inboundFileIdentifier);
    setSelectedAttachmentPages([]);
    setSelectedOrderIdentifier("");
    setOrderIdentifierInputText("");
    setSearchedOpenOrderOption(null);
    setIsOrderLookupLoading(false);
    setSelectedActivityFileType("");
    setActivityFileTypeInputText("");
    setActivityFileTypeAutocompleteResetKey(
      (currentResetKey) => currentResetKey + 1,
    );
    setOtherDocumentFileName("");
  }, []);

  /**
   * Clears the selected inbound file record.
   */
  const clearSelectedInboundFileRecord = useCallback(() => {
    setSelectedInboundFileIdentifier(null);
    setSelectedAttachmentPages([]);
  }, []);

  /**
   * Handles selecting an inbound-file section.
   * @param {string} inboundFileSectionIdentifier The section identifier.
   * @returns {void}
   */
  const handleInboundFileSectionSelect = (inboundFileSectionIdentifier) => {
    if (inboundFileSectionIdentifier === selectedInboundFileSectionIdentifier) {
      return;
    }

    clearSelectedInboundFileRecord();
    setInboundFileSearchText("");
    setSelectedInboundFileSectionIdentifier(inboundFileSectionIdentifier);
  };

  /**
   * Attaches the selected inbound file pages to the selected order activity, then removes the inbound file.
   */
  const handleLinkInboundFile = async () => {
    const payload = {
      documentType: selectedActivityFileTypeName,
      inboundFileId: parseInt(selectedInboundFileIdentifier, 10),
      uploadToOrderActivityId: parseInt(selectedOpenOrderOption.value, 10),
      pages: selectedAttachmentPages,
    };

    setIsAttachingInboundFile(true);
    try {
      await api.send(
        "POST",
        "/inboundFile/attachAndDeleteInboundFile",
        payload,
      );

      setAllInboundFileRecords((currentInboundFileRecords) => {
        return currentInboundFileRecords.filter((inboundFileRecord) => {
          return inboundFileRecord.id !== selectedInboundFileIdentifier;
        });
      });
      setSelectedInboundFileIdentifier(null);
      setSelectedAttachmentPages([]);
      setSelectedOrderIdentifier("");
      setOrderIdentifierInputText("");
      setSelectedActivityFileType("");
      setActivityFileTypeInputText("");
      setActivityFileTypeAutocompleteResetKey(
        (currentResetKey) => currentResetKey + 1,
      );
      setOtherDocumentFileName("");
      await loadInboundFileRecords({
        shouldShowRefreshLoading: true,
      });
      NotistackEnqueueSnackbar("Fax linked successfully", {
        variant: "success",
      });
    } catch {
      NotistackEnqueueSnackbar("Failed to link fax", { variant: "error" });
    } finally {
      setIsAttachingInboundFile(false);
    }
  };

  /**
   * Deletes the selected inbound file.
   * @returns {Promise<void>}
   */
  const handleDeleteInboundFile = async () => {
    const payload = {
      inboundFileId: parseInt(selectedInboundFileIdentifier, 10),
    };

    setIsDeletingInboundFile(true);

    try {
      await api.send("DELETE", `/inboundFile/${payload.inboundFileId}`);

      clearSelectedInboundFileRecord();

      setIsDeleteInboundFileDialogOpen(false);

      await loadInboundFileRecords({
        shouldShowRefreshLoading: true,
      });

      NotistackEnqueueSnackbar("Fax deleted successfully", {
        variant: "success",
      });
    } catch {
      NotistackEnqueueSnackbar("Failed to delete fax", { variant: "error" });
    } finally {
      setIsDeletingInboundFile(false);
    }
  };

  /**
   * Transfers the selected inbound file to a user's fax number.
   * @param {object} user The user receiving the fax.
   * @returns {Promise<boolean>} Whether the transfer succeeded.
   */
  const handleTransferInboundFile = async (user) => {
    const payload = Object.assign({}, selectedInboundFileRecord, {
      recipientContact: user.fax,
    });

    setIsTransferringInboundFile(true);

    try {
      await api.send("PUT", "/inboundFile", payload);

      clearSelectedInboundFileRecord();

      await loadInboundFileRecords({
        shouldShowRefreshLoading: true,
      });

      NotistackEnqueueSnackbar(
        `Fax transferred to ${User.getDisplayName(user)}`,
        {
          variant: "success",
        },
      );
      return true;
    } catch {
      NotistackEnqueueSnackbar("Failed to transfer fax", { variant: "error" });
      return false;
    } finally {
      setIsTransferringInboundFile(false);
    }
  };

  /**
   * Restores the selected inbound file.
   * @returns {Promise<void>}
   */
  const handleRestoreInboundFile = async () => {
    const payload = Object.assign({}, selectedInboundFileRecord, {
      deleted: false,
      deletedAt: null,
      deletedById: null,
    });

    setIsRestoringInboundFile(true);

    try {
      await api.send("PUT", "/inboundFile", payload);

      clearSelectedInboundFileRecord();

      await loadInboundFileRecords({
        shouldShowRefreshLoading: true,
      });

      NotistackEnqueueSnackbar("Fax restored successfully", {
        variant: "success",
      });
    } catch {
      NotistackEnqueueSnackbar("Failed to restore fax", { variant: "error" });
    } finally {
      setIsRestoringInboundFile(false);
    }
  };

  /**
   * Gets the maximum content width.
   * @returns {number} The maximum width in pixels.
   */
  const getMaxWidth = () => {
    return 2000;
  };

  /**
   * Renders the inbound-file workspace.
   * @returns {React.ReactElement} The workspace content.
   */
  const renderContent = () => {
    const inboundFileWorkspace = (
      <MUIPaper
        elevation={1}
        sx={{
          height: {
            xs: "auto",
            md: isDashboardRoute
              ? "100%"
              : InboundFilesConfiguration.desktopWorkspaceHeight,
          },
          minHeight: 0,
          overflow: {
            xs: "visible",
            md: "hidden",
          },
        }}
      >
        <MUIGrid
          container
          alignItems="stretch"
          sx={{
            height: {
              xs: "auto",
              md: "100%",
            },
            minHeight: 0,
          }}
        >
          {/* Inbox List */}
          <MUIGrid
            item
            xs={12}
            sx={{
              display: "flex",
              flexBasis: {
                xs: "100%",
                md: 400,
              },
              flexGrow: 0,
              maxHeight: {
                xs: "none",
                md: "100%",
              },
              maxWidth: {
                xs: "100%",
                md: 400,
              },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <MUIStack
              sx={{
                height: {
                  xs: 360,
                  md: "100%",
                },
                maxHeight: {
                  xs: 360,
                  md: "100%",
                },
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
                p: 2,
                width: "100%",
              }}
            >
              <MUIStack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                sx={{ mb: 2 }}
              >
                <MUIAutoComplete
                  multiple={true}
                  disableCloseOnSelect={true}
                  options={recipientContactOptions}
                  value={recipientContactOptions.filter(
                    (recipientContactOption) => {
                      return selectedRecipientContacts.includes(
                        recipientContactOption.value,
                      );
                    },
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={(option) =>
                    `${option.label} (${option.count})`
                  }
                  onChange={(event, selectedRecipientContactOptions) => {
                    const nextSelectedRecipientContacts =
                      selectedRecipientContactOptions.map(
                        (recipientContactOption) => {
                          return recipientContactOption.value;
                        },
                      );

                    clearSelectedInboundFileRecord();
                    Setting.set(
                      "inboundFiles.recipient",
                      nextSelectedRecipientContacts,
                    );
                    setSelectedRecipientContacts(nextSelectedRecipientContacts);
                  }}
                  disabled={recipientContactOptions.length === 0}
                  noOptionsText="No recipient contacts"
                  renderTags={(selectedRecipientContactOptions) => {
                    if (selectedRecipientContactOptions.length === 0) {
                      return null;
                    }

                    const selectedRecipientFileCount =
                      selectedRecipientContactOptions.reduce(
                        (fileCount, recipientContactOption) => {
                          return fileCount + recipientContactOption.count;
                        },
                        0,
                      );
                    const selectedRecipientTitle =
                      selectedRecipientContactOptions
                        .map((recipientContactOption) => {
                          return `${recipientContactOption.label} (${recipientContactOption.count})`;
                        })
                        .join("\n");
                    const selectedRecipientLabel =
                      selectedRecipientContactOptions.length === 1
                        ? selectedRecipientContactOptions[0].label
                        : `${selectedRecipientContactOptions.length} recipients`;

                    return (
                      <MUIChip
                        label={`${selectedRecipientLabel} · ${selectedRecipientFileCount} Faxes`}
                        size="small"
                        title={selectedRecipientTitle}
                        sx={{ maxWidth: "calc(100% - 8px)" }}
                      />
                    );
                  }}
                  renderOption={(optionProperties, recipientContactOption) => {
                    const listItemProperties =
                      getAutocompleteListItemProperties(optionProperties);

                    return (
                      <MUIStack
                        key={optionProperties.key}
                        component="li"
                        {...listItemProperties}
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ pr: 0.75 }}
                      >
                        <MUIBox sx={{ minWidth: 0, pr: 1 }}>
                          <MUITypography
                            component="span"
                            display="block"
                            sx={{ fontSize: 13 }}
                          >
                            {recipientContactOption.label}
                          </MUITypography>
                          <MUITypography
                            component="span"
                            display="block"
                            color="text.secondary"
                            sx={{ fontSize: 12 }}
                          >
                            {recipientContactOption.count}{" "}
                            {recipientContactOption.count === 1
                              ? "Fax"
                              : "Faxes"}
                          </MUITypography>
                        </MUIBox>
                      </MUIStack>
                    );
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiAutocomplete-inputRoot": {
                      flexWrap: "nowrap",
                      minHeight: 40,
                      overflow: "hidden",
                    },
                    "& .MuiAutocomplete-tag": {
                      flexShrink: 1,
                    },
                  }}
                  renderInput={(params) => (
                    <MUITextField
                      {...params}
                      fullWidth={true}
                      size="small"
                      label="Recipient"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <Menu
                  menuItems={[
                    <MUIMenuItem
                      disabled={
                        isInboundFileListLoading ||
                        isInboundFileRefreshLoading ||
                        !mainContext.currentUser.id
                      }
                      onClick={() =>
                        loadInboundFileRecords({
                          shouldShowRefreshLoading: true,
                          shouldShowSuccessSnackbar: true,
                        })
                      }
                    >
                      <MUIRefreshIcon fontSize="small" sx={{ mr: 1 }} />
                      Refresh
                    </MUIMenuItem>,
                    <MUIDivider />,
                    <MUIBox sx={{ px: 2, py: 1, width: 340 }}>
                      <MUITypography
                        sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}
                      >
                        Keyboard Shortcuts
                      </MUITypography>
                      <MUIStack spacing={0.75}>
                        {InboundFilesConfiguration.keyboardShortcuts.map(
                          (keyboardShortcut) => {
                            return (
                              <MUIStack
                                key={keyboardShortcut.keys}
                                direction="row"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                spacing={2}
                              >
                                <MUITypography
                                  color="text.secondary"
                                  sx={{
                                    flex: 1,
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    minWidth: 0,
                                  }}
                                >
                                  {keyboardShortcut.description}
                                </MUITypography>
                                <MUIChip
                                  label={keyboardShortcut.keys}
                                  size="small"
                                  sx={{
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    fontFamily: "monospace",
                                    fontSize: 11,
                                    height: 22,
                                  }}
                                />
                              </MUIStack>
                            );
                          },
                        )}
                      </MUIStack>
                    </MUIBox>,
                  ]}
                />
              </MUIStack>
              <MUIStack direction="row" spacing={0.75} sx={{ mb: 2 }}>
                {InboundFilesConfiguration.sections.map(
                  (inboundFileSectionConfig) => {
                    const isSelectedInboundFileSection =
                      inboundFileSectionConfig.id ===
                      selectedInboundFileSectionIdentifier;
                    const sectionRecords =
                      inboundFileRecordsBySectionIdentifier[
                        inboundFileSectionConfig.id
                      ] || [];
                    const selectedRecipientContactSet = new Set(
                      selectedRecipientContacts,
                    );
                    const recipientFilteredSectionRecordCount =
                      selectedRecipientContacts.length > 0
                        ? sectionRecords.filter((inboundFileRecord) => {
                            return selectedRecipientContactSet.has(
                              Format.phone(inboundFileRecord.recipientContact),
                            );
                          }).length
                        : 0;

                    return (
                      <MUIListItemButton
                        key={inboundFileSectionConfig.id}
                        selected={isSelectedInboundFileSection}
                        onClick={() =>
                          handleInboundFileSectionSelect(
                            inboundFileSectionConfig.id,
                          )
                        }
                        sx={{
                          borderRadius: 1,
                          flex: 1,
                          justifyContent: "center",
                          minWidth: 0,
                          px: 1,
                          py: 0.75,
                          textAlign: "center",
                        }}
                      >
                        <MUIListItemText
                          primary={`${inboundFileSectionConfig.label} (${recipientFilteredSectionRecordCount})`}
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            noWrap: true,
                          }}
                          sx={{ m: 0 }}
                        />
                      </MUIListItemButton>
                    );
                  },
                )}
              </MUIStack>
              <MUITextField
                fullWidth={true}
                size="small"
                placeholder="Type to search..."
                value={inboundFileSearchText}
                onChange={(event) =>
                  setInboundFileSearchText(event.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <MUIInputAdornment position="start">
                      <MUISearchIcon fontSize="small" />
                    </MUIInputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <MUIStack
                sx={{
                  alignItems: isInboundFileListLoading ? "center" : "stretch",
                  flex: 1,
                  height: "100%",
                  justifyContent: isInboundFileListLoading
                    ? "center"
                    : "flex-start",
                  maxHeight: "100%",
                  minHeight: 0,
                  overflowX: "hidden",
                  overflowY: "scroll",
                  pr: 0.5,
                }}
              >
                {isInboundFileListLoading ? (
                  <MUIBox sx={{ width: "100%", height: "50%" }}>
                    <Progress loading={true} message="Loading faxes..." />
                  </MUIBox>
                ) : filteredInboundFileRecords.length === 0 ? (
                  <EmptyState
                    line1="No faxes match your search."
                    line2={
                      isSelectedInboundFileSectionReadOnly
                        ? `There are no ${selectedInboundFileSectionLabel} faxes to show from the last 14 days.`
                        : "There are no faxes to show."
                    }
                  />
                ) : (
                  <MUIList disablePadding={true} sx={{ minWidth: 0 }}>
                    {filteredInboundFileRecords.map((inboundFileRecord) => {
                      const recipientContact = Format.phone(
                        inboundFileRecord.recipientContact,
                      );
                      const recipientContactOption =
                        recipientContactOptionByValue[recipientContact];
                      const inboundFileType =
                        mainContext.termsIndexed[
                          inboundFileRecord.inboundFileTypeTermId
                        ];

                      return (
                        <InboundFileListItem
                          key={inboundFileRecord.id}
                          inboundFile={inboundFileRecord}
                          inboundFileTypeName={
                            inboundFileType ? inboundFileType.name : ""
                          }
                          isSelected={
                            selectedInboundFileIdentifier ===
                            inboundFileRecord.id
                          }
                          isWaitingForProcessing={isInboundFileWaitingForProcessing(
                            inboundFileRecord,
                          )}
                          onSelect={selectInboundFileRecord}
                          recipientText={
                            recipientContactOption
                              ? recipientContactOption.label
                              : Format.phone(inboundFileRecord.recipientContact)
                          }
                        />
                      );
                    })}
                    {isSelectedInboundFileSectionReadOnly ? (
                      <MUIStack alignItems="center" sx={{ py: 1 }}>
                        <MUIChip
                          icon={<MUIInfoOutlinedIcon />}
                          label={`Not showing ${selectedInboundFileSectionLabel} faxes older than 14 days`}
                          size="small"
                          variant="outlined"
                        />
                      </MUIStack>
                    ) : null}
                  </MUIList>
                )}
              </MUIStack>
            </MUIStack>
          </MUIGrid>

          {/* Preview Area */}
          <MUIGrid
            item
            xs={12}
            sx={{
              display: "flex",
              flexBasis: {
                xs: "100%",
                md: 0,
              },
              flexGrow: {
                xs: 0,
                md: 1,
              },
              maxWidth: {
                xs: "100%",
                md: "none",
              },
              minHeight: 0,
              minWidth: 0,
              overflow: {
                xs: "visible",
                md: "hidden",
              },
            }}
          >
            <MUIStack
              sx={{
                height: {
                  xs: selectedInboundFileIdentifier
                    ? "max(720px, calc(100vh - 120px))"
                    : 360,
                  md: "100%",
                },
                minHeight: 0,
                width: "100%",
              }}
            >
              {/* Preview area */}
              <MUIStack
                sx={{
                  alignItems: "center",
                  flex: 1,
                  justifyContent: "center",
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <InboundFilePreview
                  key={selectedInboundFileIdentifier || "no-selection"}
                  inboundFile={selectedInboundFileRecord || null}
                  isPreviewUnavailable={isSelectedInboundFilePreviewUnavailable}
                  isWaitingForProcessing={isSelectedInboundFileListItemDisabled}
                  linkedOrderIdentifier={selectedLinkedOrderIdentifier}
                  onAttachmentPagesChange={setSelectedAttachmentPages}
                  shouldShowUnavailableForMissingThumbnail={
                    isSelectedInboundFileSectionReadOnly
                  }
                />
              </MUIStack>
              <MUIDivider />

              {/* Order & Document Type Selection */}
              <MUIBox sx={{ flexShrink: 0, mt: "auto", p: 2 }}>
                <MUIGrid container spacing={2} alignItems="center">
                  <MUIGrid
                    item
                    xs={12}
                    md={Number(selectedActivityFileType) === 149 ? 3.25 : 4.5}
                  >
                    <MUIBox
                      onFocus={() =>
                        startOpenOrderPolling(
                          selectedInboundFileRecipientUserProfileId,
                        )
                      }
                      onBlur={(event) => {
                        if (event.currentTarget.contains(event.relatedTarget)) {
                          return; // Focus moved within the field, not away from it.
                        }

                        stopOpenOrderPolling();
                      }}
                    >
                      <MUIAutoComplete
                        clearOnBlur={false}
                        disableClearable={true}
                        openOnFocus={true}
                        options={[
                          InboundFilesConfiguration.createNewPatientOption,
                          ...openOrderOptions,
                        ]}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        getOptionLabel={(openOrderOption) => {
                          if (typeof openOrderOption === "string") {
                            return openOrderOption;
                          }

                          return (
                            openOrderOption.inputLabel ||
                            openOrderOption.label ||
                            ""
                          );
                        }}
                        value={
                          isSelectedInboundFileSectionLinked
                            ? null
                            : selectedOpenOrderOption
                        }
                        inputValue={
                          isSelectedInboundFileSectionLinked
                            ? selectedLinkedOrderIdentifier
                              ? `#${selectedLinkedOrderIdentifier}`
                              : ""
                            : orderIdentifierInputText
                        }
                        filterOptions={(options, filterState) => {
                          const inputValue = filterState.inputValue;
                          const normalizedInputValue = inputValue
                            .trim()
                            .toLowerCase();
                          const selectedOpenOrderOptionLabel = (
                            (selectedOpenOrderOption
                              ? selectedOpenOrderOption.inputLabel
                              : "") ||
                            (selectedOpenOrderOption
                              ? selectedOpenOrderOption.label
                              : "") ||
                            ""
                          ).toLowerCase();

                          const orderOptions = options.filter(
                            (openOrderOption) => {
                              return (
                                openOrderOption.value !==
                                InboundFilesConfiguration.createNewPatientOptionValue
                              );
                            },
                          );
                          const normalizedNumericInputValue =
                            normalizedInputValue.replace(/\D/g, "");
                          const filteredOrderOptions =
                            !normalizedInputValue ||
                            normalizedInputValue ===
                              selectedOpenOrderOptionLabel
                              ? orderOptions
                              : orderOptions.filter((openOrderOption) => {
                                  return (
                                    openOrderOption.searchText.includes(
                                      normalizedInputValue,
                                    ) ||
                                    (normalizedNumericInputValue &&
                                      openOrderOption.searchText.includes(
                                        normalizedNumericInputValue,
                                      ))
                                  );
                                });
                          const linkOrderOption =
                            /^\d+$/.test(normalizedInputValue) &&
                            filteredOrderOptions.length === 0
                              ? {
                                  inputLabel: `Link to Order #${normalizedInputValue}`,
                                  isOrderLookup: true,
                                  label: `Link to Order #${normalizedInputValue}`,
                                  orderIdentifier: normalizedInputValue,
                                  searchText: normalizedInputValue,
                                  title: `Link to Order #${normalizedInputValue}`,
                                  value: `link-order-${normalizedInputValue}`,
                                }
                              : null;

                          return [
                            InboundFilesConfiguration.createNewPatientOption,
                            ...(linkOrderOption ? [linkOrderOption] : []),
                            ...filteredOrderOptions,
                          ];
                        }}
                        onChange={(event, nextOpenOrderOption) => {
                          if (!nextOpenOrderOption) {
                            setSelectedOrderIdentifier("");
                            setOrderIdentifierInputText("");
                            return;
                          }

                          if (
                            nextOpenOrderOption.value ===
                            InboundFilesConfiguration.createNewPatientOptionValue
                          ) {
                            window.open("/patient/create", "_blank");
                            return;
                          }

                          if (nextOpenOrderOption.isOrderLookup) {
                            handleOrderLookup(
                              nextOpenOrderOption.orderIdentifier,
                            );
                            return;
                          }

                          setSelectedOrderIdentifier(nextOpenOrderOption.value);
                          setOrderIdentifierInputText(
                            nextOpenOrderOption.inputLabel ||
                              nextOpenOrderOption.label,
                          );
                        }}
                        onInputChange={(event, nextInputValue, reason) => {
                          if (reason === "reset") {
                            return;
                          }

                          if (reason === "clear") {
                            setSelectedOrderIdentifier("");
                            setOrderIdentifierInputText("");
                            return;
                          }

                          setSelectedOrderIdentifier("");
                          setOrderIdentifierInputText(nextInputValue);
                        }}
                        disabled={
                          !selectedInboundFileRecord ||
                          isSelectedInboundFileListItemDisabled ||
                          isSelectedInboundFileSectionReadOnly ||
                          isOrderLookupLoading
                        }
                        loading={isOpenOrderListLoading || isOrderLookupLoading}
                        loadingText={
                          isOrderLookupLoading
                            ? "Finding order..."
                            : "Loading open orders..."
                        }
                        noOptionsText={
                          isOpenOrderListLoading
                            ? "Loading open orders..."
                            : "Type any order number"
                        }
                        renderOption={(optionProperties, openOrderOption) => {
                          const listItemProperties =
                            getAutocompleteListItemProperties(optionProperties);

                          return (
                            <li
                              key={optionProperties.key}
                              {...listItemProperties}
                            >
                              <MUIStack
                                direction={
                                  openOrderOption.value ===
                                    InboundFilesConfiguration.createNewPatientOptionValue ||
                                  openOrderOption.isOrderLookup
                                    ? "row"
                                    : "column"
                                }
                                alignItems={
                                  openOrderOption.value ===
                                    InboundFilesConfiguration.createNewPatientOptionValue ||
                                  openOrderOption.isOrderLookup
                                    ? "center"
                                    : "stretch"
                                }
                                spacing={
                                  openOrderOption.value ===
                                    InboundFilesConfiguration.createNewPatientOptionValue ||
                                  openOrderOption.isOrderLookup
                                    ? 1
                                    : 0
                                }
                                sx={{ minWidth: 0 }}
                              >
                                {openOrderOption.value ===
                                InboundFilesConfiguration.createNewPatientOptionValue ? (
                                  <MUIPersonAddIcon
                                    color="primary"
                                    fontSize="small"
                                  />
                                ) : null}
                                {openOrderOption.isOrderLookup ? (
                                  <MUILinkIcon
                                    color="primary"
                                    fontSize="small"
                                  />
                                ) : null}
                                <MUITypography variant="body2" noWrap>
                                  {openOrderOption.title}
                                </MUITypography>
                                {openOrderOption.value !==
                                  InboundFilesConfiguration.createNewPatientOptionValue &&
                                !openOrderOption.isOrderLookup ? (
                                  <MUITypography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    {openOrderOption.metaLine}
                                  </MUITypography>
                                ) : null}
                              </MUIStack>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <MUITextField
                            {...params}
                            size="small"
                            label="Order #"
                            variant="standard"
                            fullWidth={true}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isOrderLookupLoading ? (
                                    <MUICircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {isSelectedInboundFileSectionLinked &&
                                  selectedLinkedOrderIdentifier ? (
                                    <MUIIconButton
                                      size="small"
                                      title="Open order in new tab"
                                      onMouseDown={(event) =>
                                        event.preventDefault()
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenLinkedOrder(
                                          selectedLinkedOrderIdentifier,
                                        );
                                      }}
                                    >
                                      <MUIOpenInNewIcon fontSize="small" />
                                    </MUIIconButton>
                                  ) : selectedOpenOrderOption &&
                                    selectedOpenOrderPatientIdentifier ? (
                                    <MUIIconButton
                                      component="a"
                                      href={`/chart/${selectedOpenOrderPatientIdentifier}/order/${selectedOpenOrderOption.value}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      title="Open order in new tab"
                                      onMouseDown={(event) =>
                                        event.preventDefault()
                                      }
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    >
                                      <MUIOpenInNewIcon fontSize="small" />
                                    </MUIIconButton>
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            required={true}
                          />
                        )}
                      />
                    </MUIBox>
                  </MUIGrid>
                  <MUIGrid
                    item
                    xs={12}
                    md={Number(selectedActivityFileType) === 149 ? 3.25 : 4.5}
                  >
                    <MUIAutoComplete
                      key={activityFileTypeAutocompleteResetKey}
                      disableClearable={true}
                      options={activityFileTypeOptions}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      getOptionLabel={(option) => option.label}
                      value={selectedActivityFileTypeOption}
                      inputValue={activityFileTypeInputText}
                      onChange={(event, newValue) => {
                        const nextActivityFileType = newValue
                          ? newValue.value
                          : null;

                        setSelectedActivityFileType(nextActivityFileType);
                        setActivityFileTypeInputText(
                          newValue ? newValue.label : "",
                        );

                        if (Number(nextActivityFileType) !== 149) {
                          setOtherDocumentFileName("");
                        }
                      }}
                      onInputChange={(event, nextInputValue) => {
                        setActivityFileTypeInputText(nextInputValue);
                      }}
                      disabled={
                        !selectedInboundFileRecord ||
                        isSelectedInboundFileListItemDisabled ||
                        isSelectedInboundFileSectionReadOnly
                      }
                      renderInput={(params) => (
                        <MUITextField
                          {...params}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          variant="standard"
                          label="Document Type"
                          fullWidth
                          required={true}
                        />
                      )}
                    />
                  </MUIGrid>
                  {Number(selectedActivityFileType) === 149 ? (
                    <MUIGrid item xs={12} md={3}>
                      <MUITextField
                        size="small"
                        label="Document Name"
                        variant="standard"
                        fullWidth={true}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 255 }}
                        value={otherDocumentFileName}
                        onBlur={(event) =>
                          setOtherDocumentFileName(event.target.value.trim())
                        }
                        onChange={(event) =>
                          setOtherDocumentFileName(event.target.value)
                        }
                        disabled={
                          !selectedInboundFileRecord ||
                          isSelectedInboundFileListItemDisabled ||
                          isSelectedInboundFileSectionReadOnly
                        }
                        required={true}
                      />
                    </MUIGrid>
                  ) : null}
                  <MUIGrid
                    item
                    xs={12}
                    md={Number(selectedActivityFileType) === 149 ? 2.5 : 3}
                  >
                    <MUIStack direction="row" spacing={1}>
                      {isSelectedInboundFileSectionDeleted ? (
                        <MUILoadingButton
                          fullWidth={true}
                          variant="outlined"
                          color="success"
                          startIcon={<MUIRestoreIcon />}
                          onClick={handleRestoreInboundFile}
                          loading={isRestoringInboundFile}
                          disabled={
                            isRestoringInboundFile ||
                            !selectedInboundFileIdentifier
                          }
                        >
                          Restore
                        </MUILoadingButton>
                      ) : (
                        <>
                          <MUILoadingButton
                            fullWidth={true}
                            variant="contained"
                            startIcon={<MUILinkIcon />}
                            onClick={handleLinkInboundFile}
                            loading={isAttachingInboundFile}
                            disabled={
                              isAttachingInboundFile ||
                              isDeletingInboundFile ||
                              isTransferringInboundFile ||
                              isSelectedInboundFileListItemDisabled ||
                              isSelectedInboundFileSectionReadOnly ||
                              !selectedInboundFileIdentifier ||
                              !selectedOpenOrderOption ||
                              !selectedActivityFileType ||
                              !selectedActivityFileTypeName ||
                              selectedAttachmentPages.length === 0
                            }
                          >
                            Link
                          </MUILoadingButton>
                          {isSelectedInboundFileSectionInbox ? (
                            <Menu
                              disableScrollLock={true}
                              tooltip="Fax actions"
                              menuItems={[
                                <MUIMenuItem
                                  disabled={
                                    isDeletingInboundFile ||
                                    isAttachingInboundFile ||
                                    isTransferringInboundFile ||
                                    isSelectedInboundFileListItemDisabled ||
                                    !selectedInboundFileIdentifier
                                  }
                                  onClick={() =>
                                    setIsTransferInboundFileDialogOpen(true)
                                  }
                                >
                                  <MUIListItemIcon>
                                    <MUIForwardToInboxIcon fontSize="small" />
                                  </MUIListItemIcon>
                                  Transfer
                                </MUIMenuItem>,
                                <MUIMenuItem
                                  disabled={
                                    isDeletingInboundFile ||
                                    isAttachingInboundFile ||
                                    isTransferringInboundFile ||
                                    isSelectedInboundFileListItemDisabled ||
                                    !selectedInboundFileIdentifier
                                  }
                                  onClick={() =>
                                    setIsDeleteInboundFileDialogOpen(true)
                                  }
                                >
                                  <MUIListItemIcon>
                                    <MUIDeleteIcon
                                      fontSize="small"
                                      color="error"
                                    />
                                  </MUIListItemIcon>
                                  Delete
                                </MUIMenuItem>,
                              ]}
                            />
                          ) : null}
                        </>
                      )}
                    </MUIStack>
                    <DialogConfirm
                      open={isDeleteInboundFileDialogOpen}
                      onClose={() => setIsDeleteInboundFileDialogOpen(false)}
                      onConfirm={handleDeleteInboundFile}
                      header="Delete Fax"
                      text="Permanently delete this fax? You might not be able to restore it."
                      loading={isDeletingInboundFile}
                    />
                    <DialogInboundFileTransfer
                      open={isTransferInboundFileDialogOpen}
                      currentRecipientContact={
                        selectedInboundFileRecord
                          ? selectedInboundFileRecord.recipientContact
                          : ""
                      }
                      onClose={() => setIsTransferInboundFileDialogOpen(false)}
                      onTransfer={handleTransferInboundFile}
                      userOptions={transferUserOptions}
                    />
                  </MUIGrid>
                </MUIGrid>
              </MUIBox>
            </MUIStack>
          </MUIGrid>
        </MUIGrid>
      </MUIPaper>
    );

    if (isDashboardRoute === false) {
      return inboundFileWorkspace;
    }

    return (
      <MUIStack
        sx={{
          boxSizing: "border-box",
          flex: "1 1 auto",
          height: {
            xs: "auto",
            md: InboundFilesConfiguration.dashboardLayoutHeight,
          },
          minHeight: {
            xs: "auto",
            md: 0,
          },
          overflow: {
            xs: "visible",
            md: "hidden",
          },
        }}
      >
        <MUIStack sx={{ flexShrink: 0 }}>
          <WidgetDashboardRow
            activeWidget={props.activeWidget}
            dashboardWidgets={props.dashboardWidgets}
            onActiveWidgetChange={props.onActiveWidgetChange}
            updateActiveWidgetConfiguration={
              props.updateActiveWidgetConfiguration
            }
          />
        </MUIStack>
        <MUIStack
          sx={{
            flex: {
              xs: "0 0 auto",
              md: "1 1 0",
            },
            minHeight: {
              xs: "auto",
              md: 0,
            },
            overflow: {
              xs: "visible",
              md: "hidden",
            },
          }}
        >
          {inboundFileWorkspace}
        </MUIStack>
      </MUIStack>
    );
  };

  return (
    <LayerFunctional getMaxWidth={getMaxWidth} renderContent={renderContent} />
  );
}
